/**
 * EditorPage
 * Main map editor page - refactored with custom hooks and components
 */

import { floorZoneName } from '../../../../map-editor-shared/lib/zoneName'
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { dia } from '@joint/core'
import { Alert, CircularProgress, Snackbar } from '@mui/material'
import { Map as MapIcon } from '@mui/icons-material'
import ProjectPageHeader from '../../../../components/ProjectPageHeader'
import { useTheme } from '../../../../map-editor-shared/context/ThemeContext'
import { useFloorStore } from '../../../../map-editor-shared/store/floorStore'
import { useObjectTypeStore } from '../../../../map-editor-shared/store/objectTypeStore'
import { useProjectStore } from '../../../../map-editor-shared/store/projectStore'
import { useCSVStore, useCsvRuntimeStore } from '../../../../map-editor-features/csv/model/csvStore'
import { useEditorStore } from '../../../../map-editor-shared/store/editorStore'
import { ElementId, TypeId } from '../../../../map-editor-shared/types/branded'
import { useUIStore, EditorTool } from '../../../../map-editor-shared/store/uiStore'
import { EditorHeader, ViewMode } from '../../components/EditorHeader'
import { EditorModals } from '../../components/EditorModals'
import { ZoneDrawingHud, ZoneRedrawButton } from '../../components/ZoneDrawingHud'
import { polygonsOverlap } from '../../../../map-editor-features/editor/lib/zonePolygonOverlap'
import { GuideBoardDirectionHud, GuideBoardLinkButton } from '../../components/GuideBoardDirectionHud'
import { useGuideBoardDirectionLink } from '../../hooks/useGuideBoardDirectionLink'
import { isGuideBoardElement } from '../../../../map-editor-features/editor/lib/guideBoardDirectionRelations'
import {
  useZonePolygonDrawing,
  createZoneCell,
  generateEntityHandle,
  listExistingZoneNames,
  type PendingZoneInput,
} from '../../hooks/useZonePolygonDrawing'
import { ZoneCreateModal } from '../../components/ZoneCreateModal'
import { useZoneLayerRestore } from '../../hooks/useZoneLayerRestore'
import { useZoneDrawingStore } from '../../../../map-editor-shared/store/zoneDrawingStore'
import { log, LogCategory } from '../../../../map-editor-shared/lib/logger'
import { useEditorCanvas } from '../../hooks/useEditorCanvas'
import type { CanvasRelationEdit } from '../../hooks/useCanvasEditorOverlay'
import { getActiveCanvasRenderer } from '../../../../map-editor-features/viewer/canvas/activeRenderer'
import { applyRelationLinkToTarget } from './handlers/relationshipEditHandlers'
import { useCanvasPanning } from '../../hooks/useCanvasPanning'
import { useDragSelect } from '../../hooks/useDragSelect'
import { useUndoRedo } from '../../hooks/useUndoRedo'
import ResetRelationshipsControl from '../../components/ResetRelationshipsControl'
import { resetRelationships } from '../../../../map-editor-features/editor/lib/resetRelationships'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import { useThemeSync } from '../../hooks/useThemeSync'
import { useElementSelection } from '../../hooks/useElementSelection'
import { useCanvasClickSelection } from '../../hooks/useCanvasClickSelection'
import { useHybridRendering } from '../../hooks/useHybridRendering'
import { useObjectCreation } from '../../hooks/useObjectCreation'
import { useCSVProcessing } from '../../hooks/useCSVProcessing'
import { useDragAndDropMapping } from '../../hooks/useDragAndDropMapping'
import { useObjectTypeSync, applyTypeStyle } from '../../hooks/useObjectTypeSync'
import { useSmartGuides } from '../../hooks/useSmartGuides'
import { useGrouping } from '../../hooks/useGrouping'
import { useAutoGroupLights, type AutoGroupPreview } from '../../hooks/useAutoGroupLights'
import { AutoGroupLightsModal } from '../../components/AutoGroupLightsModal'
import { useClipboard } from '../../hooks/useClipboard'
import { useFloorManagement } from '../../hooks/useFloorManagement'
import { useRelationshipManagement } from '../../hooks/useRelationshipManagement'
import { useCSVAutoSave } from '../../hooks/useCSVAutoSave'
import { useGraphAutoSave } from '../../hooks/useGraphAutoSave'
import { useServerSync } from '../../hooks/useServerSync'
import { useBeforeUnload } from '../../hooks/useBeforeUnload'
import { useAPIInitialization } from '../../hooks/useAPIInitialization'
import { useLayerMappings } from '../../hooks/useLayerMappings'
import { useEntityMappingRestore } from '../../hooks/useEntityMappingRestore'
import { useTemplateInitialization } from '../../hooks/useTemplateInitialization'
import { useProjectStateReset } from '../../hooks/useProjectStateReset'
import { useProjectInitializer } from '../../hooks/useProjectInitializer'
import { useFloorSelection } from '../../hooks/useFloorSelection'
import { useSaveProject } from '../../hooks/useSaveProject'
import { useCSVUpload } from '../../hooks/useCSVUpload'
import { usePriorityZIndex } from '../../hooks/usePriorityZIndex'
import { useObjectTypeSelection } from '../../hooks/useObjectTypeSelection'
import { useElementHighlighting } from '../../hooks/useElementHighlighting'
// useRelationshipVisualization is replaced by useRelationshipOverlay (see below).
import { useRelationshipOverlay } from '../../hooks/useRelationshipOverlay'
import { useObjectInfoLabels } from '../../hooks/useObjectInfoLabels'
import {
  loadObjectLabelToggles,
  ObjectLabelToggles,
} from '../../lib/objectLabelToggleStorage'
import { ObjectType } from '../../../../map-editor-shared/store/objectTypeStore'
import { useTemplate } from '../../../../map-editor-features/template/hooks/useTemplate'
import { TemplateId } from '../../../../map-editor-features/template/lib/templateLoader'
import { TemplateRelationType } from '../../../../map-editor-entities/schema/templateSchema'
import { useParentCommunication } from '../../../../map-editor-shared/hooks/useParentCommunication'
import { useMapData, mapDataKeys, useCreateMapData, useUpdateMapData, useUpdateProject } from '../../../../map-editor-shared/hooks'
import styles from '../../EditorPage.module.css'
import '../../../../map-editor-shared/lib/testHelpers'
import { useQueryClient } from '@tanstack/react-query'

// Extracted modules
import type { EditorModalState, RelationshipEditState } from './types'
import { createInitialEditorModalState } from './state/editorModalState'
import { handleObjectUpdateFn } from './handlers/objectHandlers'
import { handleBackToProjectsFn, handleSaveModalConfirmFn } from './handlers/navigationHandlers'
import { handleErrorFn, handleExportFn } from './handlers/editorHandlers'
import { handleUploadClickFn } from './handlers/uploadHandlers'
import { useStatusInitialization } from './hooks/useStatusInitialization'
import { useRelationshipEditMode } from './hooks/useRelationshipEditMode'
import { useCanvasRotation } from './hooks/useCanvasRotation'
import { useSaveCountdown } from './hooks/useSaveCountdown'
import { CanvasArea } from './components/CanvasArea'
import { CctvRelationPanel } from '../../../../map-editor-features/cctv-relations'
import { DrawingCheckPanel } from '../../../../map-editor-features/drawing-check'
import { mapDataGetCsvFileName, mapDataGetCsvRaw } from '../../../../map-editor-shared/store/mapDataCsvHelpers'
import { MultiSelectionPanel } from '../../components/MultiSelectionPanel'
import { EditorSidebar } from '../../components/EditorSidebar'
import {
  ExcelMenu,
  applyValidationResult as applyExcelValidationResult,
  parseExcelFile,
  validateRecords as validateExcelRecords,
  ExcelParseError,
} from '../../../../map-editor-features/excel-io'
import type {
  ApplyResult as ExcelApplyResult,
  ValidationResult as ExcelValidationResult,
} from '../../../../map-editor-features/excel-io'
import {
  matchCctvIpsToElements,
  type CctvElementLike,
  type SidecarItem,
} from '../../../../map-editor-features/cctv-ip-import'
import {
  dominantIpPrefix,
  matchCctvNamesToElements,
} from '../../../../map-editor-features/cctv-name-import'
import {
  isCctvElement,
  elementPoint,
} from '../../../../map-editor-features/cctv-ip-import/lib/matcher'
import {
  buildZonePlan,
  CCTV_NAME_RE,
  type ZoneCamera,
} from '../../../../map-editor-features/zone-auto-import'
import {
  buildDongPlan,
  type SimilarityPair,
} from '../../../../map-editor-features/dong-auto-import'
import {
  cctvIconMetrics,
  collectExistingZones,
  collectPlannerCameras,
  inferNamePrefix,
  parseNvrSheetRows,
  planZonesAndNames,
  planZonesFromNvrEntries,
  type NvrEntry,
  ZoneAutoGenerateDialog,
  type ZoneAutoGenerateValues,
} from '../../../../map-editor-features/zone-auto-generate'
import {
  CCTV_ZONE_PROPERTY_KEY,
  ZONE_CCTV_PROPERTY_KEY,
  findZoneElements,
  getZonePolygon,
} from '../../../../map-editor-features/editor/lib/zoneCctvRelations'
import toolbarButtonStyles from '../../../../map-editor-features/cctv-ip-import/ui/CctvIpImportButton.module.css'
import { ResizablePanel } from '../../../../map-editor-shared/ui/ResizablePanel'
import { ObjectTypeSidebar } from '../../../../map-editor-features/objectType'
import { PropertySheetModal } from '../../components/PropertySheetModal'
import { EditorGuide, useGuidedMapStore } from '../../../../map-editor-features/guided-map'
import { EditorErrorBoundary } from '../../../../map-editor-shared/ui/EditorErrorBoundary'
import { zoneApi } from '../../../../map-editor-shared/api/zoneApi'
import { dongApi } from '../../../../map-editor-shared/api/dongApi'
import {
  createDongLabelCell,
  listExistingDongNames,
} from '../../lib/createDongLabelCell'
import { computeLabelFontSize, computeRelationStrokeWidth } from '../../lib/parkingScaleMetrics'
import { routeLayerToCategory } from '../../../../map-editor-shared/lib/layerUtils'
import { DxfParserService } from '../../../../services/DxfParserService'
import { CctvIpPrefixDialog } from '../../components/CctvIpPrefixDialog'
import { mapEditorService } from '../../../../map-editor-shared/api/services/mapEditorService'

interface EditorPageProps {
  /**
   * 라우트 없이 다른 페이지(가이드 셋업)에 그대로 끼워 넣을 때 쓰는 폴백 값.
   * URL 파라미터(:projectId/:mapId)가 있으면 그쪽이 항상 우선한다 — 기존
   * /project/:projectId/map-editor/:mapId 라우트로 진입할 땐 동작이 그대로다.
   * RoiWorkView 에서 쓰는 것과 동일한 패턴.
   */
  projectId?: string
  mapId?: string
}

export default function EditorPage({ projectId: propProjectId, mapId: propMapId }: EditorPageProps = {}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const params = useParams<{ projectId: string; mapId: string }>()
  const urlProjectId = params.projectId || propProjectId || ''
  const mapId = params.mapId || propMapId || ''
  const { theme, toggleTheme } = useTheme()
  const { currentFloor, updateFloor, floors, updateFloorMapData, setCurrentFloor } = useFloorStore()
  // 구역 이름 앞에 붙는 층 이름 (B1_1, B1_2 …)
  const currentFloorName = floors.find(f => String(f.id) === String(currentFloor))?.name
  // CRITICAL: Use mapId from URL as source of truth for currentLot.
  // Reading from Zustand store causes a race condition: when navigating between projects,
  // the store's currentLot lags behind the URL by one render cycle. If React Query returns
  // cached map data before the store syncs, useFloorSelection creates floors under the
  // WRONG lotId — contaminating the previous project with the new project's data.
  const currentLot = mapId || null
  const currentLotData = useProjectStore(state => state.getLotById(mapId || ''))
  const setCurrentLot = useObjectTypeStore(state => state.setCurrentLot)
  const setCurrentProjectId = useObjectTypeStore(state => state.setCurrentProjectId)
  const setSiteProjectId = useObjectTypeStore(state => state.setSiteProjectId)
  const loadTypes = useObjectTypeStore(state => state.loadTypes)
  const loadMappings = useObjectTypeStore(state => state.loadMappings)

  // Parent communication hook for iframe messaging (not used in standalone mode)
  useParentCommunication()

  // Use mapId from URL as projectId for API calls
  const projectId = mapId ? parseInt(mapId, 10) : null
  const { data: mapDataArray, isLoading: isMapDataLoading } = useMapData(projectId)

  // Fix: 프로젝트 전환 시 이전 프로젝트의 React Query 캐시를 제거하여 stale 데이터 방지
  const prevProjectIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (projectId !== prevProjectIdRef.current) {
      if (prevProjectIdRef.current !== null) {
        queryClient.removeQueries({
          queryKey: mapDataKeys.list(prevProjectIdRef.current),
        })
      }
      prevProjectIdRef.current = projectId
    }
  }, [projectId, queryClient])

  // Sync current lot from URL parameter to Zustand stores (for other consumers)
  useEffect(() => {
    if (!mapId) return
    const storeCurrentLot = useProjectStore.getState().currentLot
    if (mapId === storeCurrentLot) return
    useProjectStore.getState().setCurrentLot(mapId)
    setCurrentLot(mapId)
  }, [mapId, setCurrentLot])

  // Fix: API 응답의 linked_project_id 검증으로 캐시된 잘못된 데이터 차단
  const mapData = useMemo(() => {
    if (!mapDataArray || mapDataArray.length === 0 || !projectId) return null
    const record = mapDataArray[0]
    if (record.linked_project_id !== projectId) return null
    return record.map_data
  }, [mapDataArray, projectId])
  const createMapDataMutation = useCreateMapData()
  const updateMapDataMutation = useUpdateMapData()
  const updateProjectMutation = useUpdateProject()

  // Refs
  const canvasRef = useRef<HTMLDivElement>(null)
  const previousLotRef = useRef<string | null>(null)

  // State
  const types = useObjectTypeStore(state => state.types)
  const mappings = useObjectTypeStore(state => state.mappings)
  const layerTypeMappings = useObjectTypeStore(state => state.layerTypeMappings)
  const setFile = useCSVStore(s => s.setFile)
  const setUploadState = useCSVStore(s => s.setUploadState)
  const rawData = useCSVStore(s => s.rawData)
  const parsedData = useCSVStore(s => s.parsedData)
  const groupedLayers = useCSVStore(s => s.groupedLayers)
  const selectedLayers = useCSVStore(s => s.selectedLayers)
  // One-shot CCTV IP stash from the latest DXF conversion (auto IP wiring).
  const pendingCctvIps = useCsvRuntimeStore(s => s.cctvIps)
  const [selectedObjectType, setSelectedObjectType] = useState<ObjectType | null>(null)
  const [zoom, setZoom] = useState(1)
  const [elementCount, setElementCount] = useState(0)
  // 우측 패널(객체 리스트/속성)이 읽는다 — 핸들러들이 setter 로 동기화.
  const [objectsByLayer, setObjectsByLayer] = useState<Map<string, dia.Element[]>>(new Map())
  const [loadedFileName, setLoadedFileName] = useState<string | null>(null)

  // Selection state backed by Zustand editorStore (prevents parent re-render cascade)
  const selectionElementId = useEditorStore(s => s.selection.selectedElementId)
  const selectionElementIds = useEditorStore(s => s.selection.selectedElementIds)
  const selectionTypeId = useEditorStore(s => s.selection.selectedTypeId)
  const selectionState = useMemo(() => ({
    elementId: selectionElementId,
    typeId: selectionTypeId,
    elementIds: new Set(selectionElementIds),
  }), [selectionElementId, selectionElementIds, selectionTypeId])

  const [currentTool, setCurrentTool] = useState<EditorTool>('pan')
  const [dataVersion, setDataVersion] = useState(0)
  const [pendingGraphJson, setPendingGraphJson] = useState<any | null>(null)
  // Modal state (reset when mapId changes)
  const [modalState, setModalState] = useState<EditorModalState>(createInitialEditorModalState)

  useEffect(() => {
    if (!mapId) return
    setModalState(createInitialEditorModalState())
    useCsvRuntimeStore.getState().clearAllCsvRuntime()
    useUIStore.getState().resetCSVUI()
    useEditorStore.getState().resetEditor()
  }, [mapId])

  const setSelectedElementId = useCallback((value: string | null) => {
    useEditorStore.getState().setSelectedElementId(value ? ElementId(value) : null)
  }, [])
  const setSelectedElementIds = useCallback((value: Set<string>) => {
    useEditorStore.getState().setSelectedElementIds(Array.from(value).map(ElementId))
  }, [])
  const setSelectedTypeId = useCallback((value: string | null) => {
    useEditorStore.getState().setSelectedTypeId(value ? value as TypeId : null)
  }, [])

  const [autoNavigate, setAutoNavigate] = useState(false)

  // Relationship Edit Mode State
  const [relationshipEditState, setRelationshipEditState] = useState<RelationshipEditState>({
    editing: false,
    relationKey: null,
    propertyKey: null,
    targetIds: []
  })

  // 좌측 객체 타입 패널(추가/수정/배치) — 툴바 버튼으로만 열고 닫는다.
  const [typePanelOpen, setTypePanelOpen] = useState(false)

  // CCTV 관계(조명 순차 점등 체인) 패널 — 툴바 버튼으로 열고 닫는다.
  const [cctvRelOpen, setCctvRelOpen] = useState(false)
  const [drawingCheckOpen, setDrawingCheckOpen] = useState(false)

  const [viewMode, setViewMode] = useState<ViewMode>('edit')
  // 맵이 처음 렌더링되는 순간(기존 맵을 바로 열었을 때) 딱 한 번 뷰어 모드로
  // 전환한다 — 시뮬레이터처럼 깔끔하게 보여주고 편집은 "에디터 모드" 버튼으로
  // 직접 선택하게 한다. 이후 사용자가 직접 고른 모드는(편집 중 요소를 지웠다
  // 다시 추가하는 등으로) elementCount 가 오르내려도 다시 뷰어로 강제 전환하지
  // 않는다.
  //
  // 가이드 진행 중(phase==='editor')에는 절대 끼어들지 않는다 — DXF→레이어
  // 매핑 직후 elementCount 가 0보다 커지는 순간 여기서 뷰어로 전환하며 가이드를
  // end() 해버리면, 조명 그룹핑/PDF 매핑/관계 생성 단계로 못 넘어가고 가이드가
  // 거기서 끝나버린다(레이어 매핑 이후 진행이 멈추는 버그). 가이드는 'done'
  // 단계의 "뷰어 열기" 버튼(뷰어 페이지로 이동)으로 끝에서만 뷰어를 보여준다.
  const hasAutoShownViewerRef = useRef(false)
  useEffect(() => {
    if (
      elementCount > 0 &&
      !hasAutoShownViewerRef.current &&
      useGuidedMapStore.getState().phase === 'idle'
    ) {
      hasAutoShownViewerRef.current = true
      setViewMode('viewer')
    }
  }, [elementCount])

  // Canvas rotation (0, 90, 180, 270)
  const { rotation, handleRotate } = useCanvasRotation(canvasRef)

  // Hover State (backed by Zustand)
  const hoveredElementId = useEditorStore(s => s.selection.hoveredElementId)

  const { template } = useTemplate(currentLotData?.templateName as TemplateId)

  const [mutableRelationTypes, setMutableRelationTypes] = useState<Record<string, TemplateRelationType>>({})

  // Canvas 오버레이(CANVAS_EDITOR flag)용 관계 편집 어댑터. onTargetClick 은
  // 아래에서 정의되는 handleObjectUpdate 등을 써야 하므로 ref 로 우회한다.
  const relationTargetClickRef = useRef<(targetId: string) => void>(() => {})
  const canvasRelationEdit = useMemo<CanvasRelationEdit>(() => ({
    editing: relationshipEditState.editing,
    targetIds: relationshipEditState.targetIds,
    onTargetClick: (id: string) => relationTargetClickRef.current(id),
  }), [relationshipEditState])

  const { paper, graph, handleZoomIn, handleZoomOut, handleFitToScreen } = useEditorCanvas(
    canvasRef,
    setZoom,
    undefined,
    currentTool,
    selectedObjectType,
    () => setSelectedObjectType(null),
    mutableRelationTypes,
    canvasRelationEdit
  )

  // Floor management hook
  const {
    isRestoring,
  } = useFloorManagement({
    graph,
    paper,
    selectedElementId: selectionState.elementId,
    projectId: projectId || 0,
    currentLot,
    currentLotData,
    updateMapDataMutation,
    createMapDataMutation,
    editorMapData: mapData,
    setObjectsByLayer,
    setElementCount,
  })

  // Save project hook - handles complete save workflow
  const {
    handleSave,
    isSaving,
    savedThumbnail,
    savedMapId,
    showSaveSuccessModal,
    setShowSaveSuccessModal
  } = useSaveProject({
    graph,
    paper,
    currentFloor,
    currentLot,
    projectId,
    floors,
    hasMapData: !!mapData,
    selectedElementId: selectionState.elementId,
    createMapDataMutation,
    updateMapDataMutation,
    updateProjectMutation,
    onError: (error) => setModalState(prev => ({ ...prev, error: { show: true, message: error.message } })),
  })

  // Handler for updating object properties
  const handleObjectUpdate = useCallback((id: string, updates: Partial<any>) => {
    // 타입 변경 감지 — data.typeId 가 바뀌면 objectTypeId prop·스타일·그룹을 함께
    // 옮긴다. PropertyEditor/객체 속성 시트/JSON 편집 어느 경로든 여기 한 곳에서
    // 처리한다. 사이드바 그룹 키(elementLayerKey)와 export 라우팅 모두
    // data.layer 우선이므로 layer/objectType 도 새 타입 이름으로 바꿔야 실제로
    // "해당 타입 그룹" 으로 이동한다.
    if (graph && updates.data?.typeId) {
      const cell = graph.getCell(id)
      if (cell?.isElement()) {
        const oldData = (cell.get('data') || {}) as { typeId?: string; layer?: string; objectType?: string }
        const newTypeId = String(updates.data.typeId)
        if (newTypeId !== String(oldData.typeId ?? '')) {
          const type = useObjectTypeStore.getState().types.find(t => t.id === newTypeId)
          if (type) {
            const oldKey = oldData.layer || oldData.objectType || oldData.typeId || 'misc'
            const enriched = {
              ...updates,
              data: { ...updates.data, objectType: type.name, layer: type.name },
            }
            handleObjectUpdateFn(graph, setDataVersion, id, enriched)
            cell.prop('objectTypeId', newTypeId)
            // 색/아이콘 갱신 + 필요 시 rect↔image 셰이프 교체(remove/add).
            applyTypeStyle(graph, newTypeId, type)
            // 그룹 이동. 셰이프 교체 경로는 graph add/remove 리스너가 이미
            // 옮겼을 수 있으므로 id 기준 dedup 으로 안전하게 처리한다.
            const moved = (graph.getCell(id) as dia.Element) || cell
            setObjectsByLayer(prev => {
              const next = new Map(prev)
              const from = next.get(oldKey)
              if (from) {
                const filtered = from.filter(e => e.id !== id)
                if (filtered.length === 0) next.delete(oldKey)
                else next.set(oldKey, filtered)
              }
              const to = next.get(type.name) || []
              if (!to.some(e => e.id === id)) next.set(type.name, [...to, moved])
              return next
            })
            return
          }
        }
      }
    }
    handleObjectUpdateFn(graph, setDataVersion, id, updates)
  }, [graph, setDataVersion])

  // ── Excel Import/Export wiring ────────────────────────────────────────────
  const [excelImportResult, setExcelImportResult] =
    useState<ExcelValidationResult | null>(null)
  const [excelSkippedSheets, setExcelSkippedSheets] = useState<string[]>([])
  const [excelSnackbar, setExcelSnackbar] = useState<{
    open: boolean
    severity: 'success' | 'info' | 'error'
    message: string
  }>({ open: false, severity: 'info', message: '' })
  // "CCTV & 구역 매핑" 통합 버튼의 진행 단계. idle 이 아니면 전체화면 오버레이로
  // 차단한다(각 단계가 백엔드 PDF 분석을 돌아 수 초 걸림).
  const [mappingStep, setMappingStep] = useState<'idle' | 'ip' | 'name' | 'zone'>('idle')
  // 단독 「CCTV IP 매핑」 진행 중 — mappingStep 과 분리한다. EditorGuide 는
  // mappingStep 의 idle→busy→idle 을 통합 PDF 파이프라인 완료로 관찰하므로
  // 단독 IP 매핑이 그 신호를 내면 가이드가 잘못 넘어간다.
  const [cctvIpBusy, setCctvIpBusy] = useState(false)

  // CCTV IP 앞자리 설정 다이얼로그 — 프로젝트 생성 때 빼먹었을 때 에디터에서
  // 바로 고칠 수 있게 한다(툴바 버튼 + [1/3 IP] 실패 시 자동 오픈).
  const [ipPrefixDialogOpen, setIpPrefixDialogOpen] = useState(false)
  const [ipPrefixSaving, setIpPrefixSaving] = useState(false)

  const handleSnackbarClose = useCallback(
    (_event?: unknown, reason?: string) => {
      // Don't auto-close on clickaway — only manual close button or autoHide
      if (reason === 'clickaway') return
      setExcelSnackbar(prev => ({ ...prev, open: false }))
    },
    []
  )

  const handleExcelMessage = useCallback(
    (kind: 'info' | 'success' | 'error', text: string) => {
      setExcelSnackbar({ open: true, severity: kind, message: text })
    },
    []
  )

  const handleSaveCctvIpPrefix = useCallback(
    async (prefix: string) => {
      if (!projectId) return
      setIpPrefixSaving(true)
      try {
        await mapEditorService.updateProject(projectId, { cctv_ip_prefix: prefix })
        // 로컬 스토어에도 반영 — 다이얼로그 재오픈/대시보드 수정 폼 프리필용.
        useProjectStore.getState().updateLot(String(projectId), { cctvIpPrefix: prefix })
        setIpPrefixDialogOpen(false)
        setExcelSnackbar({
          open: true,
          severity: 'success',
          message: `CCTV IP 앞자리(${prefix}) 저장 완료 — DXF 변환을 다시 실행하면 CCTV IP가 추출됩니다.`,
        })
      } catch (e) {
        setExcelSnackbar({
          open: true,
          severity: 'error',
          message: e instanceof Error ? e.message : 'CCTV IP 앞자리 저장에 실패했습니다.',
        })
      } finally {
        setIpPrefixSaving(false)
      }
    },
    [projectId]
  )

  const handleExcelFileSelected = useCallback(
    async (file: File) => {
      if (!graph) return
      // Open the modal in "loading" state (result === null).
      setExcelImportResult(null)
      setExcelSkippedSheets([])
      setModalState(prev => ({
        ...prev,
        excelImport: { show: true, fileName: file.name },
      }))
      try {
        const { records, skippedSheets } = await parseExcelFile(file, types)
        const result = validateExcelRecords(records, graph, types)
        setExcelImportResult(result)
        setExcelSkippedSheets(skippedSheets)
      } catch (e) {
        const msg =
          e instanceof ExcelParseError
            ? e.message
            : e instanceof Error
              ? e.message
              : String(e)
        // Close modal and surface error via Snackbar (replaces previous ErrorModal route).
        setModalState(prev => ({ ...prev, excelImport: { show: false } }))
        setExcelSnackbar({
          open: true,
          severity: 'error',
          message: `Excel 파일 처리 실패: ${msg}`,
        })
      }
    },
    [graph, types]
  )

  // Resolver passed to <ExcelMenu>. Mirrors PropertyEditor.tsx steps 3+4 so
  // CSV-imported elements (whose typeId lives in entity-handle / layer-type
  // mapping stores, not on the element itself) are NOT silently dropped from
  // the export. Without this, a 188-entity CSV import with 1 layer mapping
  // exported as 0 rows.
  const resolveTypeIdForExport = useCallback<NonNullable<React.ComponentProps<typeof ExcelMenu>['resolveTypeId']>>(
    (element) => {
      const data = element.get('data') as
        | { entityHandle?: string; layer?: string }
        | undefined
      if (!data) return undefined
      const handle = data.entityHandle
      if (handle) {
        const m = mappings.find(mm => mm.entityHandle === handle)
        if (m) return m.typeId
      }
      const layer = data.layer
      if (layer && currentFloor) {
        const lm = layerTypeMappings.find(
          mm => String(mm.floorId) === String(currentFloor) && mm.layerName === layer
        )
        if (lm) return lm.typeId
      }
      // 층 id 가 안 맞아도(저장된 층 id ≠ layer_type_mappings.floor_id) 같은 layer
      // 이름의 매핑이 있으면 그걸 쓴다 — 없으면 시트/엑셀에서 객체가 통째로 빠진다.
      if (layer) {
        const lm = layerTypeMappings.find(mm => mm.layerName === layer)
        if (lm) return lm.typeId
        // 마지막 폴백: CCTV layer 는 이름으로 CCTV 타입을 고른다 (IP 매핑이 typeId 를
        // 채우기 전의 카메라도 시트에 나오게).
        const category = routeLayerToCategory(layer)
        if (category === 'cctv' || category === 'warningCctv') {
          const cctvTypes = types.filter(t => /cctv/i.test(t.name))
          const picked =
            category === 'warningCctv'
              ? cctvTypes.find(t => t.name.includes('단방향'))
              : cctvTypes.find(t => t.name.includes('조명')) ?? cctvTypes.find(t => !t.name.includes('단방향'))
          return (picked ?? cctvTypes[0])?.id
        }
      }
      return undefined
    },
    [mappings, layerTypeMappings, currentFloor, types]
  )

  const handleExcelImportApply = useCallback(
    (result: ExcelValidationResult): ExcelApplyResult => {
      if (!graph) return { applied: 0, skipped: result.records.length }
      const apply = applyExcelValidationResult(result, graph, handleObjectUpdate)
      // Reset result so the next upload starts fresh.
      setExcelImportResult(null)
      setExcelSkippedSheets([])
      const tail =
        result.summary.unmatched > 0
          ? `, ${result.summary.unmatched}건 미매칭 제외`
          : ''
      setExcelSnackbar({
        open: true,
        severity: 'success',
        message: `${apply.applied}건 적용 완료${tail}`,
      })
      return apply
    },
    [graph, handleObjectUpdate]
  )

  // Apply core: match sidecar items to placed CCTV elements and write ipAddress
  // (typing untyped cameras) through handleObjectUpdate. Shared by the manual
  // file-import button and the automatic post-conversion wiring. Returns the
  // match result so callers can decide whether the apply "took".
  const applyCctvIpItems = useCallback(
    (items: SidecarItem[], opts?: { silent?: boolean }) => {
      if (!graph) return null
      const elements = graph.getElements()
      const byId = new Map(elements.map(el => [String(el.id), el]))
      const result = matchCctvIpsToElements(
        items,
        elements as unknown as CctvElementLike[]
      )
      // Assign each matched camera the CCTV object type that matches its OWN
      // layer (단방향 → warningCctv, 조명/전방위 → cctv). Imported cameras carry
      // their type via data.layer (which drives the icon) + entityHandle mapping,
      // NOT data.typeId — so an earlier global "조명" fallback mislabeled every
      // 단방향 카메라 as 조명 (icon 단방향 ↔ OBJECT TYPE 조명 mismatch). We also
      // self-heal that legacy state: a camera currently typed as the WRONG CCTV
      // type gets corrected. A deliberate non-CCTV type is left untouched.
      const cctvTypes = types.filter(
        t =>
          Array.isArray(t.properties) &&
          t.properties.some(p => p.key === 'ipAddress') &&
          /cctv/i.test(t.name)
      )
      const lightCctvType =
        cctvTypes.find(t => t.name.includes('조명')) || cctvTypes[0] || null
      const warningCctvType =
        cctvTypes.find(t => t.name.includes('단방향')) || null
      const omniCctvType =
        cctvTypes.find(t => t.name.includes('전방위')) || lightCctvType
      // 1순위: IP 대역(현장 컨벤션) — 172.19.35.x = 단방향, 172.19.31~34.x = 전방위.
      // 대역이 규칙 밖이면 기존 layer/objectType 라우팅으로 폴백.
      const ipDirection = (ip: string): 'warning' | 'omni' | null => {
        const m = ip.match(/^172\.19\.(\d{1,3})\./)
        if (!m) return null
        const n = Number(m[1])
        if (n === 35) return 'warning'
        if (n >= 31 && n <= 34) return 'omni'
        return null
      }
      const resolveCctvType = (data: Record<string, unknown>, ip: string) => {
        const dir = ipDirection(ip)
        if (dir === 'warning') return warningCctvType || lightCctvType
        if (dir === 'omni') return omniCctvType
        const layer = typeof data.layer === 'string' ? data.layer : ''
        const objectType =
          typeof data.objectType === 'string' ? data.objectType : ''
        const isWarning =
          routeLayerToCategory(layer) === 'warningCctv' ||
          routeLayerToCategory(objectType) === 'warningCctv'
        return isWarning ? warningCctvType || lightCctvType : lightCctvType
      }
      let typedCount = 0
      for (const u of result.updates) {
        const el = byId.get(u.id)
        if (!el) continue
        const data = (el.get('data') ?? {}) as Record<string, unknown>
        const props = (data.properties ?? {}) as Record<string, unknown>
        const currentType =
          typeof data.typeId === 'string'
            ? types.find(t => t.id === data.typeId)
            : undefined
        const desiredType = resolveCctvType(data, u.ip)
        // Re-type when untyped/stale, or when the current CCTV type disagrees
        // with the camera's layer. Non-CCTV manual types are preserved.
        const needsType =
          !currentType ||
          (/cctv/i.test(currentType.name) &&
            !!desiredType &&
            currentType.id !== desiredType.id)
        const newData: Record<string, unknown> = {
          ...data,
          properties: { ...props, ipAddress: u.ip },
        }
        if (needsType && desiredType) {
          newData.typeId = desiredType.id
          typedCount += 1
        }
        handleObjectUpdate(u.id, { data: newData })
      }
      if (!opts?.silent) {
        if (result.matched === 0) {
          let message: string
          if (result.cctvCount === 0) {
            message = 'CCTV 객체가 없습니다. CCTV 레이어를 먼저 배치하세요.'
          } else if (!result.aligned) {
            message = `CCTV ${result.cctvCount}개와 좌표 정렬에 실패했습니다. 같은 도면에서 만든 JSON인지 확인하세요.`
          } else {
            message = '매칭된 IP가 없습니다.'
          }
          setExcelSnackbar({ open: true, severity: 'error', message })
        } else {
          const parts: string[] = []
          if (typedCount > 0) {
            parts.push(`${typedCount}개 CCTV 타입 자동 지정`)
          }
          if (result.unmatchedIps.length > 0) {
            parts.push(`${result.unmatchedIps.length}개 미매칭`)
          }
          const tail = parts.length > 0 ? ` (${parts.join(', ')})` : ''
          setExcelSnackbar({
            open: true,
            severity: 'success',
            message: `CCTV IP ${result.matched}/${result.totalItems}개 적용 완료${tail}`,
          })
        }
      }
      return result
    },
    [graph, handleObjectUpdate, types]
  )


  // ── 동/건물 라벨 자동 생성 (DXF 사이드카) ──────────────────────────────────
  // 동 정보는 DXF 전용 레이어(큰 글자)에서 추출돼 <csv>.buildings.json 으로
  // 저장되고, 좌표는 CCTV IP 사이드카와 같은 좌표계라 IP↔에디터 CCTV 대응쌍으로
  // 맞춘 similarity 변환을 그대로 적용한다. 통합 매핑 4단계와 단독 버튼이 공유.
  const applyDongLabelsFromDxf = useCallback(
    async (ipItems: SidecarItem[], warn: string[]) => {
      const siteId = urlProjectId ? parseInt(urlProjectId, 10) : NaN
      if (!graph || !projectId || !siteId) return { created: 0, buildings: 0 }
      const buildings = await DxfParserService.fetchServerBuildingLabels(siteId)
      let created = 0
      if (buildings.length > 0) {
        const cctvByIp = new Map<string, { x: number; y: number }>()
        for (const el of graph.getElements()) {
          const data = (el as unknown as CctvElementLike).get('data')
          if (!isCctvElement(data)) continue
          const props = (data?.properties ?? {}) as Record<string, unknown>
          const ip = typeof props.ipAddress === 'string' ? props.ipAddress.trim() : ''
          if (!ip) continue
          const pt = elementPoint(data)
          if (!pt) continue
          cctvByIp.set(ip, { x: pt.x, y: pt.y })
        }
        const cctvPairs: SimilarityPair[] = []
        for (const it of ipItems) {
          const ed = cctvByIp.get(String(it.ip))
          if (!ed) continue
          cctvPairs.push({ pdf: { x: it.x, y: it.y }, editor: { x: ed.x, y: ed.y } })
        }
        try {
          const dongPlan = buildDongPlan(buildings, cctvPairs, listExistingDongNames(graph))
          if (dongPlan.dongs.length > 0) {
            const dongType = await dongApi.ensureDongObjectType(projectId)
            const dongFontSize = computeLabelFontSize(graph)
            for (const d of dongPlan.dongs) {
              const cell = createDongLabelCell({
                name: d.name,
                point: d.point,
                objectTypeId: dongType.id,
                entityHandle: generateEntityHandle(),
                fontSize: dongFontSize,
              })
              if (!cell) continue
              graph.addCell(cell)
              created += 1
            }
          }
          if (dongPlan.skippedExisting.length > 0) {
            warn.push(`기존동 스킵 ${dongPlan.skippedExisting.length}개`)
          }
        } catch {
          warn.push('동 좌표정렬 실패(CCTV IP 부족)')
        }
      }
      return { created, buildings: buildings.length }
    },
    [graph, urlProjectId, projectId]
  )

  // 단독 「동 가져오기」 — PDF 업로드 없이 DXF 사이드카만으로 동 라벨을 배치.
  // 좌표 정렬에 CCTV IP 대응쌍이 필요하므로 CCTV IP 매핑이 먼저 돼 있어야 한다.
  const [dongBusy, setDongBusy] = useState(false)
  const handleDongFromDxf = useCallback(async () => {
    if (!graph) return
    const siteId = urlProjectId ? parseInt(urlProjectId, 10) : NaN
    if (!siteId) {
      setExcelSnackbar({ open: true, severity: 'error', message: '현장 ID를 찾을 수 없습니다.' })
      return
    }
    setDongBusy(true)
    try {
      const ipItems = await DxfParserService.fetchServerCctvIps(siteId)
      if (!ipItems.length) {
        setExcelSnackbar({
          open: true,
          severity: 'error',
          message: '서버에 저장된 CCTV IP가 없습니다. 먼저 DXF를 변환하세요(동 좌표 정렬에 필요).',
        })
        return
      }
      const warn: string[] = []
      const dong = await applyDongLabelsFromDxf(ipItems as SidecarItem[], warn)
      if (dong.buildings === 0) {
        setExcelSnackbar({
          open: true,
          severity: 'error',
          message: '서버에 저장된 동/건물 라벨이 없습니다. DXF를 다시 변환하면 buildings 사이드카가 생성됩니다.',
        })
        return
      }
      const warnTail = warn.length > 0 ? ` (${warn.join(', ')})` : ''
      setExcelSnackbar({
        open: true,
        severity: dong.created > 0 ? 'success' : 'info',
        message: `동 라벨 ${dong.created}/${dong.buildings}개 생성${warnTail} · Save 버튼으로 저장 필요`,
      })
    } catch (e) {
      setExcelSnackbar({
        open: true,
        severity: 'error',
        message: `동 가져오기 실패: ${e instanceof Error ? e.message : String(e)}`,
      })
    } finally {
      setDongBusy(false)
    }
  }, [graph, urlProjectId, applyDongLabelsFromDxf])

  // ── CCTV & 구역 통합 매핑 ───────────────────────────────────────────────────
  // 도면 PDF 1개로 세 단계를 순서대로 실행한다(데이터 의존이라 순서 강제):
  //   1) CCTV IP — 서버 사이드카(extract_cctv_ips.py 산출)에서 ipAddress 적용
  //   2) CCTV 이름 — 같은 PDF 의 이름 라벨을 IP 기준으로 카메라에 매핑
  //   3) 구역 — 같은 PDF 의 빨간 구역선으로 zone 생성(이름 다수결로 명명)
  // 각 단계 0건이면 즉시 중단하고 그때까지 적용분을 요약한다. 영속화는 기존과
  // 동일하게 수동 Save 필요(자동 저장 안 함).
  const handleCctvZoneMapping = useCallback(
    async (file: File) => {
      // 아래 가드에서 걸려 조기 return 하더라도 mappingStep 을 반드시 idle 로
      // 되돌려야 한다 — EditorGuide 는 idle→(다른 값)→idle 사이클을 "PDF 파이프라인
      // 시작→완료"로 관찰해 다음 단계로 넘어간다(EditorGuide.tsx). 가드 실패 시에도
      // idle 을 벗어나지 않으면 그 신호가 오지 않아 가이드가 PDF 단계에서 멈춘다.
      setMappingStep('ip')
      const summary: string[] = []
      try {
        if (!graph) return
        const siteId = urlProjectId ? parseInt(urlProjectId, 10) : NaN
        if (!siteId) {
          setExcelSnackbar({ open: true, severity: 'error', message: '현장 ID를 찾을 수 없습니다.' })
          return
        }
        if (!projectId || !currentFloor) {
          setExcelSnackbar({ open: true, severity: 'error', message: '맵/층 정보를 찾을 수 없습니다.' })
          return
        }

        // 1) CCTV IP — 서버 사이드카
        const ipItems = await DxfParserService.fetchServerCctvIps(siteId)
        if (!ipItems.length) {
          setExcelSnackbar({
            open: true,
            severity: 'error',
            message: '[1/3 IP] 서버에 저장된 CCTV IP가 없습니다. 먼저 DXF를 변환하세요(현장 CCTV IP 앞자리 설정 필요).',
          })
          // 흔한 원인이 IP 앞자리 미설정이라, 여기서 바로 고칠 수 있게 설정
          // 다이얼로그를 함께 연다(이미 설정돼 있으면 값 확인 후 닫으면 됨).
          setIpPrefixDialogOpen(true)
          return
        }
        const ipResult = applyCctvIpItems(ipItems as SidecarItem[], { silent: true })
        if (!ipResult || ipResult.matched === 0) {
          const msg = !ipResult
            ? 'CCTV IP 적용 실패'
            : ipResult.cctvCount === 0
              ? 'CCTV 객체가 없습니다. CCTV 레이어를 먼저 배치하세요.'
              : !ipResult.aligned
                ? `CCTV ${ipResult.cctvCount}개와 좌표 정렬 실패(같은 도면 JSON 확인)`
                : '매칭된 IP가 없습니다.'
          setExcelSnackbar({ open: true, severity: 'error', message: `[1/3 IP] ${msg}` })
          return
        }
        summary.push(`IP ${ipResult.matched}/${ipResult.totalItems}`)

        // 2) CCTV 이름 — 같은 PDF
        setMappingStep('name')
        const nameElements = graph.getElements()
        const prefix = dominantIpPrefix(nameElements as unknown as CctvElementLike[])
        const mapping = await DxfParserService.extractCctvNames(siteId, file, prefix ?? undefined)
        const nameResult = matchCctvNamesToElements(
          mapping.items,
          nameElements as unknown as CctvElementLike[]
        )
        if (nameResult.matched === 0) {
          const msg =
            nameResult.cctvWithIpCount === 0
              ? 'IP가 매핑된 CCTV가 없습니다.'
              : '매칭된 CCTV가 없습니다(같은 현장 도면 PDF 확인).'
          setExcelSnackbar({
            open: true,
            severity: 'error',
            message: `[2/3 이름] ${msg} · 적용됨: ${summary.join(', ')}`,
          })
          return
        }
        const nameById = new Map(nameElements.map(el => [String(el.id), el]))
        for (const u of nameResult.updates) {
          const el = nameById.get(u.id)
          if (!el) continue
          const data = (el.get('data') ?? {}) as Record<string, unknown>
          const props = (data.properties ?? {}) as Record<string, unknown>
          handleObjectUpdate(u.id, {
            data: { ...data, properties: { ...props, name: u.name } },
          })
        }
        summary.push(`이름 ${nameResult.matched}/${nameResult.totalItems}`)

        // 3) 구역 — 같은 PDF (빨간 외곽선 → zone)
        setMappingStep('zone')
        const regions = await DxfParserService.extractZoneRegions(siteId, file)
        const cameras: ZoneCamera[] = []
        for (const el of graph.getElements()) {
          const data = (el as unknown as CctvElementLike).get('data')
          if (!isCctvElement(data)) continue
          const props = (data?.properties ?? {}) as Record<string, unknown>
          const name = typeof props.name === 'string' ? props.name.trim() : ''
          if (!CCTV_NAME_RE.test(name)) continue
          const pt = elementPoint(data)
          if (!pt) continue
          cameras.push({ name, x: pt.x, y: pt.y })
        }

        const warn: string[] = []
        let created = 0
        if (regions.zones.length > 0) {
          const plan = buildZonePlan(regions, cameras, listExistingZoneNames(graph), currentFloorName)
          if (plan.zones.length > 0) {
            const zoneType = await zoneApi.ensureZoneObjectType(projectId)
            for (const zone of plan.zones) {
              const cell = createZoneCell({
                points: zone.points,
                name: zone.name,
                objectTypeId: zoneType.id,
                entityHandle: generateEntityHandle(),
                strokeColor: zoneType.color,
                memo: undefined,
                fontSize: computeLabelFontSize(graph),
              })
              if (!cell) continue
              graph.addCell(cell)
              created += 1
            }
          }
          const mixedZones = plan.zones.filter(z => z.mixedVoteCount > 0)
          if (mixedZones.length > 0) warn.push(`이름혼입 ${mixedZones.length}개`)
          const noVoteCount = plan.zones.filter(z => z.noVotes).length
          if (noVoteCount > 0) warn.push(`이름없는구역 ${noVoteCount}개`)
          if (plan.skippedExisting.length > 0) warn.push(`기존구역 스킵 ${plan.skippedExisting.length}개`)
          if (plan.uncoveredSegments.length > 0) {
            warn.push(`구역선없음(수동필요): ${plan.uncoveredSegments.map(s => `${s}구역`).join(', ')}`)
          }
        }
        summary.push(`구역 ${created}개`)

        // 4) 동/건물 라벨 — DXF 사이드카(서버) + CCTV IP↔에디터 정렬 재사용.
        const dong = await applyDongLabelsFromDxf(ipItems as SidecarItem[], warn)
        if (dong.buildings > 0) {
          summary.push(`동 ${dong.created}개`)
        }
        const warnTail = warn.length > 0 ? ` (${warn.join(', ')})` : ''
        setExcelSnackbar({
          open: true,
          severity: 'success',
          message: `CCTV & 구역 매핑 완료 — ${summary.join(' · ')}${warnTail} · Save 버튼으로 저장 필요`,
        })
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        const tail = summary.length > 0 ? ` · 적용됨: ${summary.join(', ')}(Save 필요)` : ''
        setExcelSnackbar({
          open: true,
          severity: 'error',
          message: `CCTV & 구역 매핑 실패: ${msg}${tail}`,
        })
      } finally {
        setMappingStep('idle')
      }
    },
    [graph, urlProjectId, projectId, currentFloor, applyCctvIpItems, handleObjectUpdate, applyDongLabelsFromDxf, currentFloorName]
  )

  // ── CCTV IP 매핑 (DXF 사이드카) ─────────────────────────────────────────────
  // DXF 변환 시 서버가 저장한 CCTV IP 사이드카(<csv>.cctvips.json)를 불러와
  // 배치된 CCTV 점군과 정렬해 ipAddress 만 적용한다. 통합 매핑(위) 1단계와
  // 같은 소스 — DXF 라벨이 PDF 텍스트 추출보다 정확해서 단독 버튼도 이 경로만
  // 쓴다(기존 PDF 업로드 방식은 제거). 영속화는 수동 Save.
  const handleCctvIpFromDxf = useCallback(async () => {
    if (!graph) return
    const siteId = urlProjectId ? parseInt(urlProjectId, 10) : NaN
    if (!siteId) {
      setExcelSnackbar({ open: true, severity: 'error', message: '현장 ID를 찾을 수 없습니다.' })
      return
    }
    setCctvIpBusy(true)
    try {
      const items = await DxfParserService.fetchServerCctvIps(siteId)
      if (items.length === 0) {
        setExcelSnackbar({
          open: true,
          severity: 'error',
          message: '서버에 저장된 CCTV IP가 없습니다. 먼저 DXF를 변환하세요(현장 CCTV IP 앞자리 설정 필요).',
        })
        // 흔한 원인이 IP 앞자리 미설정이라 바로 고칠 수 있게 설정 다이얼로그를 연다.
        setIpPrefixDialogOpen(true)
        return
      }
      applyCctvIpItems(items as SidecarItem[])
    } catch (e) {
      setExcelSnackbar({
        open: true,
        severity: 'error',
        message: `CCTV IP 매핑 실패: ${e instanceof Error ? e.message : String(e)}`,
      })
    } finally {
      setCctvIpBusy(false)
    }
  }, [graph, urlProjectId, applyCctvIpItems])

  // ── 구역 자동 생성 (NVR 용량 기준) ─────────────────────────────────────────
  // 도면 PDF 의 구역선 없이, "NVR 용량 / 카메라 대당 용량" 만으로 미배정 CCTV 를
  // 용량 한도까지 최대로 묶어 원 구역을 만들고 이름을 순차 부여한다.
  const [zoneAutoGenOpen, setZoneAutoGenOpen] = useState(false)
  const [zoneAutoGenBusy, setZoneAutoGenBusy] = useState(false)

  // 다이얼로그가 열릴 때만 그래프를 훑는다(대형 맵에서 매 렌더 전체 스캔 방지).
  const zoneAutoGenInfo = useMemo(() => {
    if (!zoneAutoGenOpen || !graph) {
      return {
        pending: { omni: 0, oneway: 0 },
        total: { omni: 0, oneway: 0 },
        existingZoneCount: 0,
        defaultPrefix: '',
      }
    }
    const existingZones = collectExistingZones(graph)
    const cameras = collectPlannerCameras(graph, existingZones)
    const unassigned = cameras.filter(c => c.zoneNumber === null)
    const count = (list: typeof cameras) => ({
      omni: list.filter(c => c.kind === 'omni').length,
      oneway: list.filter(c => c.kind === 'oneway').length,
    })
    return {
      pending: count(unassigned),
      total: count(cameras),
      existingZoneCount: existingZones.length,
      defaultPrefix: inferNamePrefix(cameras.map(c => c.name)) ?? '',
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneAutoGenOpen, graph, dataVersion])

  // NVR 원격등록 템플릿(.xls) 기준 구역 생성 — IP 로 카메라를 찾고 CH NAME 으로
  // 이름을 바꾼 뒤 이름의 구역 번호끼리 묶는다. 용량 추정과 달리 파일이 정답이라
  // 같은 번호의 기존 구역은 지우고 다시 만든다(재실행 가능).
  const runNvrTemplateZones = useCallback(
    async (files: File[]) => {
      if (!graph || !projectId) return
      const XLSX = await import('xlsx')
      const entries: NvrEntry[] = []
      for (const file of files) {
        const book = XLSX.read(await file.arrayBuffer(), { type: 'array' })
        for (const sheetName of book.SheetNames) {
          const rows = XLSX.utils.sheet_to_json<unknown[]>(book.Sheets[sheetName], {
            header: 1,
            blankrows: false,
          })
          entries.push(...parseNvrSheetRows(rows))
        }
      }
      if (entries.length === 0) {
        setExcelSnackbar({
          open: true,
          severity: 'error',
          message: '템플릿에서 IP / CH NAME 열을 찾지 못했습니다.',
        })
        return
      }

      const { margin } = cctvIconMetrics(graph)
      const cameras = collectPlannerCameras(graph, [])
      const plan = planZonesFromNvrEntries(cameras, entries, { margin })
      if (plan.zones.length === 0) {
        setExcelSnackbar({
          open: true,
          severity: 'error',
          message: `이 맵에서 템플릿 IP 와 일치하는 CCTV 를 찾지 못했습니다 (템플릿 ${entries.length}줄). 먼저 CCTV IP 매핑이 필요합니다.`,
        })
        return
      }

      // 같은 번호의 기존 구역 제거 — 재실행 시 중복 생성을 막는다.
      const numbers = new Set(plan.zones.map(z => z.number))
      const stale = collectExistingZones(graph).filter(z => numbers.has(z.number))
      const staleIds = new Set(stale.map(z => z.id))
      if (stale.length > 0) {
        graph.removeCells(
          findZoneElements(graph).filter(el => staleIds.has(String(el.id)))
        )
      }

      const zoneType = await zoneApi.ensureZoneObjectType(projectId)
      const zoneIdByCamera = new Map<string, string>()
      let created = 0
      for (const zone of plan.zones) {
        const cell = createZoneCell({
          points: zone.points,
          name: floorZoneName(currentFloorName, zone.number),
          objectTypeId: zoneType.id,
          entityHandle: generateEntityHandle(),
          strokeColor: zoneType.color,
          memo: undefined,
          fontSize: computeLabelFontSize(graph),
        })
        if (!cell) continue
        const data = (cell.get('data') ?? {}) as Record<string, unknown>
        const props = (data.properties ?? {}) as Record<string, unknown>
        cell.set('data', {
          ...data,
          properties: { ...props, [ZONE_CCTV_PROPERTY_KEY]: zone.memberIds },
        })
        graph.addCell(cell)
        created += 1
        for (const id of zone.memberIds) zoneIdByCamera.set(id, String(cell.id))
      }

      const nameById = new Map(plan.names.map(n => [n.id, n.name]))
      const elementById = new Map(graph.getElements().map(el => [String(el.id), el]))
      for (const id of new Set([...zoneIdByCamera.keys(), ...nameById.keys()])) {
        const element = elementById.get(id)
        if (!element) continue
        const data = (element.get('data') ?? {}) as Record<string, unknown>
        const props = { ...((data.properties ?? {}) as Record<string, unknown>) }
        const zoneId = zoneIdByCamera.get(id)
        if (zoneId) props[CCTV_ZONE_PROPERTY_KEY] = zoneId
        const name = nameById.get(id)
        if (name) props.name = name
        handleObjectUpdate(id, { data: { ...data, properties: props } })
      }

      const warn: string[] = []
      if (plan.unmatchedIps.length > 0) warn.push(`미일치 IP ${plan.unmatchedIps.length}개(다른 층 포함)`)
      if (plan.malformedNames > 0) warn.push(`이름 규격 외 ${plan.malformedNames}줄`)
      const warnTail = warn.length > 0 ? ` (${warn.join(', ')})` : ''
      setExcelSnackbar({
        open: true,
        severity: 'success',
        message: `템플릿 기준 구역 생성 완료 — 구역 ${created}개 · 배정 ${zoneIdByCamera.size}대 · 이름 ${plan.names.length}개${warnTail} · Save 버튼으로 저장 필요`,
      })
    },
    [graph, projectId, handleObjectUpdate, currentFloorName]
  )

  const handleZoneAutoGenerate = useCallback(
    async (values: ZoneAutoGenerateValues) => {
      if (!graph) return
      if (!projectId) {
        setExcelSnackbar({ open: true, severity: 'error', message: '맵 정보를 찾을 수 없습니다.' })
        return
      }
      setZoneAutoGenBusy(true)
      try {
        if (values.mode === 'nvrTemplate') {
          await runNvrTemplateZones(values.files)
          return
        }
        // 다시 만들기: 기존 구역을 지우고 모든 카메라를 미배정·이름 없음으로 본다.
        // 카메라 소속/이름 초기화는 아래에서 새 값과 함께 한 번에 쓴다.
        if (values.rebuild) {
          graph.removeCells(findZoneElements(graph))
        }
        const existingZones = values.rebuild ? [] : collectExistingZones(graph)
        const cameras = collectPlannerCameras(graph, existingZones).map(c =>
          values.rebuild ? { ...c, name: '', zoneNumber: null } : c
        )
        const { margin, rowTolerance } = cctvIconMetrics(graph)
        const plan = planZonesAndNames(
          cameras,
          { ...values, targetZoneCount: values.targetZoneCount ?? undefined },
          {
            existingZoneNumbers: existingZones.map(z => z.number),
            margin,
            rowTolerance,
            namePrefix: values.namePrefix,
          }
        )

        if (plan.zones.length === 0 && plan.names.length === 0) {
          setExcelSnackbar({
            open: true,
            severity: 'info',
            message: '구역 미배정 CCTV와 이름 없는 CCTV가 없습니다 — 변경 사항 없음',
          })
          return
        }

        const zoneType = await zoneApi.ensureZoneObjectType(projectId)
        const zoneIdByCamera = new Map<string, string>()
        let created = 0
        for (const zone of plan.zones) {
          const cell = createZoneCell({
            points: zone.points,
            name: floorZoneName(currentFloorName, zone.number),
            objectTypeId: zoneType.id,
            entityHandle: generateEntityHandle(),
            strokeColor: zoneType.color,
            memo: undefined,
            fontSize: computeLabelFontSize(graph),
          })
          if (!cell) continue
          // 소속은 memberIds 가 정답이다 — 원이 겹칠 수 있어 폴리곤 포함 판정
          // (linkZoneToContainedCctvs)으로 다시 계산하면 용량이 넘칠 수 있다.
          const data = (cell.get('data') ?? {}) as Record<string, unknown>
          const props = (data.properties ?? {}) as Record<string, unknown>
          cell.set('data', {
            ...data,
            properties: { ...props, [ZONE_CCTV_PROPERTY_KEY]: zone.memberIds },
          })
          graph.addCell(cell)
          created += 1
          for (const id of zone.memberIds) zoneIdByCamera.set(id, String(cell.id))
        }

        // 카메라의 소속(zoneId)과 새 이름을 한 번의 업데이트로 적용한다.
        const nameById = new Map(plan.names.map(n => [n.id, n.name]))
        const elementById = new Map(graph.getElements().map(el => [String(el.id), el]))
        const touched = new Set([
          ...zoneIdByCamera.keys(),
          ...nameById.keys(),
          ...(values.rebuild ? cameras.map(c => c.id) : []),
        ])
        for (const id of touched) {
          const element = elementById.get(id)
          if (!element) continue
          const data = (element.get('data') ?? {}) as Record<string, unknown>
          const props = { ...((data.properties ?? {}) as Record<string, unknown>) }
          if (values.rebuild) {
            // 지운 구역을 가리키던 소속과 옛 이름을 비운다 — 새 값이 있으면 아래서 덮는다.
            delete props[CCTV_ZONE_PROPERTY_KEY]
            props.name = ''
          }
          const zoneId = zoneIdByCamera.get(id)
          if (zoneId) props[CCTV_ZONE_PROPERTY_KEY] = zoneId
          const name = nameById.get(id)
          if (name) props.name = name
          handleObjectUpdate(id, { data: { ...data, properties: props } })
        }

        const warn: string[] = []
        if (plan.unplaceable.length > 0) {
          warn.push(`용량초과 미배정 ${plan.unplaceable.length}대`)
        }
        if (plan.strayInsideCount > 0) {
          warn.push(`배정 원 겹침 ${plan.strayInsideCount}대(소속은 memberIds 기준으로 유지)`)
        }
        if (!values.namePrefix) warn.push('접두어 없어 이름 생략')
        const warnTail = warn.length > 0 ? ` (${warn.join(', ')})` : ''
        setExcelSnackbar({
          open: true,
          severity: 'success',
          message: `구역 자동 생성 완료 — 구역 ${created}개 · 배정 ${zoneIdByCamera.size}대 · 이름 ${plan.names.length}개${warnTail} · Save 버튼으로 저장 필요`,
        })
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        setExcelSnackbar({ open: true, severity: 'error', message: `구역 자동 생성 실패: ${msg}` })
      } finally {
        setZoneAutoGenBusy(false)
        setZoneAutoGenOpen(false)
      }
    },
    [graph, projectId, handleObjectUpdate, runNvrTemplateZones, currentFloorName]
  )

  // Auto-apply: after a DXF conversion stashes CCTV IPs, write them to the CCTV
  // elements once those elements actually render. CRITICAL: csvRenderer adds the
  // cells via `graph.addCells(..., { silent: true })`, so NO graph event fires and
  // dataVersion does NOT bump when the cameras appear. Watching dataVersion alone
  // therefore misses the render entirely. Instead we poll (rAF-friendly interval)
  // until CCTV elements exist, then apply once and drain. Subsequent layer mapping
  // does not recreate cells (see decideRecreateCells), so the applied ipAddress
  // survives.
  useEffect(() => {
    if (pendingCctvIps.length === 0) return
    if (!graph) return
    let cancelled = false
    let attempts = 0
    const MAX_ATTEMPTS = 40 // ~10s at 250ms — covers analyze→convert→render latency
    const hasCctv = () =>
      graph.getElements().some(el => {
        const data = (el.get('data') ?? {}) as Record<string, unknown>
        const haystack = [data.layer, data.typeId, data.objectType, data.type]
          .filter((v): v is string => typeof v === 'string')
          .join(' ')
        return /cctv/i.test(haystack)
      })
    const tick = () => {
      if (cancelled) return
      if (!hasCctv()) {
        if (attempts++ < MAX_ATTEMPTS) setTimeout(tick, 250)
        return // cameras not rendered yet — keep polling
      }
      const result = applyCctvIpItems(pendingCctvIps)
      if (result && result.matched > 0) {
        useCsvRuntimeStore.getState().setCctvIps([]) // applied → drain
      } else if (attempts++ < MAX_ATTEMPTS) {
        setTimeout(tick, 250) // cameras present but 0 matched — retry briefly
      }
    }
    tick()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCctvIps, graph])

  // Relationship management hook — 우측 패널(EditorSidebar)의 관계 핸들러 + 자동
  // 관계 생성 confirm.
  const {
    handleAutoLinkConfirm,
    handleUpdateRelationType,
    handleDeleteRelationType,
    handleUnlink,
    handleAutoLink,
    handleRelationEditModeChange,
  } = useRelationshipManagement({
    graph,
    paper,
    selectedElementId: selectionState.elementId,
    template,
    types,
    mutableRelationTypes,
    setMutableRelationTypes,
    handleObjectUpdate,
    relationshipEditState,
    setRelationshipEditState,
  })

  // Reset all state when switching projects
  useProjectStateReset({
    currentLot,
    graph,
    previousLotRef,
    setCurrentFloor,
    setElementCount,
    setObjectsByLayer,
    setLoadedFileName,
    setSelectedElementId,
    setSelectedElementIds,
    setSelectedTypeId,
    setPendingGraphJson,
  })

  // Initialize API: Set project ID and load object types
  useAPIInitialization({
    projectId,
    urlProjectId,
    setCurrentProjectId,
    setSiteProjectId,
    loadTypes,
  })

  // Load layer mappings when floor changes
  useLayerMappings({
    currentFloor,
    projectId,
    loadMappings,
  })

  // Restore entity-level mappings from layer-level mappings on reload.
  // Without this, `mappings` (local-only) stays empty after page refresh and
  // PropertyEditor shows "No type assigned" for every CSV-imported element.
  // `graph` is provided so the dashboard re-entry path (where `parsedData` is
  // not re-populated) can fall back to extracting entities from cell.data.
  useEntityMappingRestore({
    currentFloor,
    parsedEntities: parsedData?.entities,
    graph,
  })

  // Restore persisted Zone polygons for the current floor
  useZoneLayerRestore({
    graph,
    projectId,
    floorId: currentFloor,
  })

  // Handle floor selection and API synchronization
  useFloorSelection({
    currentLot,
    mapData,
  })

  // Initialize mutableRelationTypes from template
  useTemplateInitialization({
    template,
    currentLotData,
    setMutableRelationTypes,
  })

  // Paper interactivity is managed by the currentTool useEffect below (line ~605)

  // Auto-save hooks
  useCSVAutoSave({
    rawData,
    parsedData,
    groupedLayers,
    selectedLayers,
    loadedFileName,
    currentFloor,
    isRestoring,
    updateFloorMapData,
  })

  useGraphAutoSave({
    graph,
    currentFloor,
    isRestoring,
    updateFloorMapData,
  })

  useServerSync({
    projectId,
    currentLot,
    currentLotData,
    floors,
    updateMapDataMutation,
  })

  useBeforeUnload({ graph })

  const handleError = (error: Error) => {
    handleErrorFn(setModalState, error)
  }

  // 안내현황판 좌/우 영역 드래그 관계 맺기 (자동 관계 생성 버튼에서 진입)
  const guideBoardDirectionLink = useGuideBoardDirectionLink({
    paper,
    graph,
    canvasRef,
    onComplete: (result) => {
      // 그래프 cell data 가 바뀌었으니 관계 시각화/라벨 갱신을 위해 dataVersion bump.
      setDataVersion((v) => v + 1)
      const total = result.leftDirectionIds.length + result.rightDirectionIds.length
      setModalState((prev) => ({
        ...prev,
        error: {
          show: true,
          message: `안내현황판 관계 ${total}개 생성 (왼쪽 ${result.leftDirectionIds.length} · 오른쪽 ${result.rightDirectionIds.length})`,
        },
      }))
    },
    onError: (message) =>
      setModalState((prev) => ({ ...prev, error: { show: true, message } })),
  })

  const handleOpenAutoLinkModal = () => {
    // 미리보기(반경 원·연결선)는 편집 캔버스에 그려진다. 다시 들어온 맵은 뷰어 모드로 열려
    // 편집 캔버스가 숨겨져 있어 미리보기가 안 보였다 — 열기 전에 에디터 모드로 바꾼다.
    if (viewMode === 'viewer') setViewMode('edit')
    setModalState((prev) => ({ ...prev, autoLink: true }))
  }

  // 자동 관계 생성 적용 직후 결과를 검수/수정할 수 있게 속성 시트를 연다.
  const [propertySheetOpen, setPropertySheetOpen] = useState(false)

  // 가이드 맵 생성 위저드(에디터 단계) 활성 여부.
  const guideActive = useGuidedMapStore(s => s.phase === 'editor')

  // 속성 시트의 저장 — 기존 저장 흐름을 그대로 쓰되 성공 모달은 띄우지 않는다
  // (시트가 자체적으로 "저장됐습니다 + 변경 요약"을 표시). 실패는 throw 해서
  // 시트가 에러를 표시하고 변경 목록을 유지하게 한다.
  const handleSheetSave = useCallback(async () => {
    const ok = await handleSave()
    setShowSaveSuccessModal(false)
    if (!ok) throw new Error('저장에 실패했습니다. 오류 내용을 확인해 주세요.')
  }, [handleSave, setShowSaveSuccessModal])
  const handleAutoLinkConfirmThenSheet = useCallback(
    async (
      adjustedDistances: Record<string, number>,
      enabledRelations: Record<string, boolean>
    ) => {
      await handleAutoLinkConfirm(adjustedDistances, enabledRelations)
      setDataVersion(version => version + 1)
      setPropertySheetOpen(true)
      // 가이드 위저드가 켜져 있으면 관계 생성 단계 완료 통지(아니면 no-op).
      useGuidedMapStore.getState().completeStep('relations')
    },
    // handleAutoLinkConfirm 은 훅에서 매 렌더 재생성되므로 그대로 의존한다.
    [handleAutoLinkConfirm]
  )

  const handleExport = useCallback(() => {
    handleExportFn({ currentFloor, graph, updateFloorMapData, setModalState })
  }, [currentFloor, graph, updateFloorMapData])

  // Object type selection hook — 툴바 "🧩 객체 타입" 버튼으로 여는 좌측 타입
  // 패널(ObjectTypeSidebar)의 선택 핸들러 + 캔버스 배치/선택 부수효과.
  const { handleTypeSelect } = useObjectTypeSelection({
    graph,
    paper,
    selectedTypeId: selectionState.typeId,
    setSelectedObjectType,
    setSelectedTypeId,
    setSelectedElementId,
    setSelectedElementIds,
    projectId,
  })

  // Memoize Set → Array conversion to prevent unnecessary re-renders
  const selectedElementIdsArray = useMemo(
    () => Array.from(selectionState.elementIds),
    [selectionState.elementIds]
  )

  // Element highlighting effects (multi-select and hover)
  useElementHighlighting({
    graph,
    paper,
    selectedElementIds: selectedElementIdsArray,
    hoveredElementId
  })

  // Relationship visualization is now handled by useRelationshipOverlay below.
  // The previous useRelationshipVisualization hook drew red graph-cell links
  // (strokeWidth=3, outgoing only, no incoming) which (a) read as "too thick"
  // to users, (b) failed to surface incoming relationships, and (c) added
  // joint Link cells into the graph that risked leaking into graph.toJSON().
  // useRelationshipOverlay supersedes it: thinner SVG lines on a paper-viewport
  // overlay layer, color-coded per relationKey, with both outgoing AND incoming
  // (incl. inversePropertyKey) and bidirectional pairs collapsed to a dashed line.
  // useRelationshipVisualization({...}) — disabled, kept commented for git history.

  // Color-coded SVG overlay for outgoing + incoming relationships of the
  // current selection (single or multi). Renders into paper.viewport so it
  // tracks zoom/pan, and never adds graph cells — graph.toJSON() stays clean.
  useRelationshipOverlay(paper, graph, selectionState.elementIds, mutableRelationTypes)

  // ID/IP/NAME label overlay under each object. Same viewport-layer pattern
  // as useRelationshipOverlay, with toggle persistence in localStorage so the
  // user's last choice survives a refresh.
  const [labelToggles, setLabelToggles] = useState<ObjectLabelToggles>(() =>
    loadObjectLabelToggles(),
  )
  useObjectInfoLabels(paper, graph, currentFloor, labelToggles)

  // CSV Processing - with success callback to update state
  const handleCSVSuccess = useCallback((elementCount: number, objectsByLayer: Map<string, dia.Element[]>) => {
    setElementCount(elementCount)
    setObjectsByLayer(objectsByLayer)
  }, [])

  const { processCSVData } = useCSVProcessing(graph, paper, handleCSVSuccess, handleError)

  const {
    csvInputRef,
    triggerFileUpload,
    handleFileChange,
    handleClearCanvas,
  } = useCSVUpload({
    graph,
    processCSVData,
    setErrorModal: (error: { show: boolean; message: string }) =>
      setModalState((prev) => ({ ...prev, error })),
    setElementCount,
    setObjectsByLayer,
  })

  useDragAndDropMapping(paper, graph, currentFloor || 'default', handleError)

  // Stable selection handler (useCallback prevents effect re-registration cascades)
  const handleSelectionChange = useCallback((elementId: string | null) => {
    setSelectedElementId(elementId)
  }, [setSelectedElementId])

  const clearMultiSelection = useCallback(() => {
    setSelectedTypeId(null)
    setSelectedElementIds(new Set())
  }, [setSelectedTypeId, setSelectedElementIds])

  const { handleBlankClick } = useElementSelection(
    graph,
    paper,
    selectionState.elementId,
    handleSelectionChange,
    clearMultiSelection,
    selectionState.elementIds.size > 1,
    currentTool
  )

  // Native pointer click selection — single source of truth for canvas click events.
  // onSelect: select element + clear type selection
  // onDeselect: clear selection + clear multi-selection + clear highlights
  // NOTE: clearMultiSelection() 을 호출하면 setSelectedElementIds(new Set()) →
  // store action 이 빈 배열일 때 selectedElementId 까지 null 로 덮어씀.
  // handleSelectionChange 가 이미 selectedElementIds=[elementId] 로 single 만 남기므로
  // 추가로 multi-clear 할 필요 없음. typeId 만 정리해 placement 모드 비활성화.
  const handleCanvasSelect = useCallback((elementId: string) => {
    handleSelectionChange(elementId)
    setSelectedTypeId(null)
  }, [handleSelectionChange, setSelectedTypeId])

  // 객체 수정 시트에서 행 클릭 → 그 객체가 보이는 캔버스 중앙에 오도록 이동하고 선택 표시.
  const handleSheetFocusObject = useCallback((elementId: string) => {
    if (!graph) return
    const cell = graph.getCell(elementId)
    if (!cell || !cell.isElement()) return

    // CANVAS_EDITOR 모드에서는 화면을 그리는 주체가 Canvas 렌더러다(paper 는
    // 얼려져 숨어 있다). paper 를 움직여도 화면은 그대로이므로 렌더러에 맡긴다 —
    // 확대 기준(짧은 변의 1/8, 최대 4x, 축소 없음)은 아래 paper 경로와 동일하다.
    const renderer = getActiveCanvasRenderer()
    if (renderer?.focusElement(elementId)) {
      setZoom(renderer.getScale())
      handleCanvasSelect(elementId)
      return
    }

    if (!paper) return
    const container = paper.el.parentElement
    if (container) {
      const { width, height } = container.getBoundingClientRect()
      const bbox = (cell as dia.Element).getBBox()
      const center = bbox.center()
      // 객체가 화면 짧은 변의 1/8 정도 크기로 보이도록 확대(최대 4x — 휠 줌 상한과 동일).
      // 이미 더 확대돼 있으면 그대로 둔다.
      const fitScale = Math.min(width, height) / (Math.max(bbox.width, bbox.height, 1) * 8)
      const scale = Math.min(4, Math.max(paper.scale().sx, fitScale))
      paper.scale(scale, scale)
      paper.translate(width / 2 - center.x * scale, height / 2 - center.y * scale)
      setZoom(scale)
    }
    // 뷰포트 컬링이 pan/zoom 뒤 rAF 에서 SVG 뷰를 마운트하므로, 그 다음 프레임에
    // 선택해야 하이라이트(외곽선)가 대상 요소에 붙는다. 즉시 선택하면 뷰가 없어
    // bbox 핸들만 떠서 실제 객체 모양과 다르게 보인다.
    requestAnimationFrame(() => requestAnimationFrame(() => handleCanvasSelect(elementId)))
  }, [graph, paper, handleCanvasSelect])

  const handleCanvasToggleSelect = useCallback((elementId: string, add: boolean) => {
    if (add) {
      useEditorStore.getState().addSelectedElementId(ElementId(elementId))
      setSelectedElementId(elementId)
    } else {
      useEditorStore.getState().removeSelectedElementId(ElementId(elementId))
      // If deselected the current single selection, clear it
      if (selectionState.elementId === elementId) {
        const remaining = selectedElementIdsArray.filter(id => id !== elementId)
        setSelectedElementId(remaining.length > 0 ? remaining[0] : null)
      }
    }
  }, [selectionState.elementId, selectedElementIdsArray, setSelectedElementId])

  // Sync selection store with deletion: when an element is removed (e.g. via
  // Delete/Backspace), drop its id from both single and multi selection so the
  // right sidebar PropertyEditor and MultiSelectionPanel don't keep stale info.
  // Also drop it from `objectsByLayer` so the right-panel layer list counts
  // reflect the deletion immediately — useHybridRendering only refreshes
  // objectsByLayer on full CSV/JSON re-render, not on per-element removal.
  // Mirrors the "remaining first" pattern from handleCanvasToggleSelect above
  // and the objectsByLayer prune pattern from handleGroup below.
  const handleElementDelete = useCallback((deletedId: string) => {
    const remainingMulti = selectedElementIdsArray.filter(id => id !== deletedId)

    if (remainingMulti.length !== selectedElementIdsArray.length) {
      setSelectedElementIds(new Set(remainingMulti))
    }

    if (selectionState.elementId === deletedId) {
      setSelectedElementId(remainingMulti.length > 0 ? remainingMulti[0] : null)
    }

    setObjectsByLayer(prev => {
      let changed = false
      const next = new Map(prev)
      for (const [layer, elements] of next.entries()) {
        const filtered = elements.filter(el => (el.id as string) !== deletedId)
        if (filtered.length !== elements.length) {
          changed = true
          if (filtered.length > 0) {
            next.set(layer, filtered)
          } else {
            next.delete(layer)
          }
        }
      }
      return changed ? next : prev
    })
  }, [
    selectionState.elementId,
    selectedElementIdsArray,
    setSelectedElementId,
    setSelectedElementIds,
    setObjectsByLayer,
  ])

  // select-by-layer-modifier: Ctrl/Cmd+click handler — replace current selection
  // with every element sharing the clicked element's data.layer. Group elements
  // are intentionally excluded so the click doesn't sweep up CCTV groups, etc.
  const handleSelectByLayer = useCallback((layer: string) => {
    if (!graph) return
    const ids = graph.getElements()
      .filter(el => {
        const data = el.get('data') ?? {}
        return data.layer === layer && data.isGroup !== true
      })
      .map(el => el.id as string)

    if (ids.length === 0) return  // no-op if nothing matches (defensive)

    setSelectedElementIds(new Set(ids))
    // Anchor the current single selection on the first match so subsequent
    // Shift/Ctrl toggles have a sensible focus and panels stay in sync.
    setSelectedElementId(ids[0])
  }, [graph, setSelectedElementIds, setSelectedElementId])

  // Panning hook — only handles drag panning, NOT click selection/deselection
  useCanvasPanning(paper, graph, canvasRef, undefined, rotation, selectedObjectType, currentTool)

  useDragSelect(paper, graph, canvasRef, currentTool, setSelectedElementIds)

  const { undo, redo, checkpoint } = useUndoRedo(graph, setElementCount)

  // Grouping hook
  const { groupElements, addToGroup, ungroupElements, isGroupElement, canGroup } = useGrouping(graph)

  // 조명 자동 그룹핑 — 미리보기 모달 state.
  const { buildPreview: buildAutoGroupPreview, clusterByThreshold } = useAutoGroupLights(graph)
  const [autoGroupPreview, setAutoGroupPreview] = useState<AutoGroupPreview | null>(null)

  // Clipboard hook (copy/paste)
  const { handleCopy, handlePaste } = useClipboard(
    graph,
    selectedElementIdsArray,
    currentFloor,
    setElementCount
  )

  const isGroupSelected = !!(selectionState.elementId && isGroupElement(selectionState.elementId))
  const canGroupSelection = canGroup(selectedElementIdsArray)

  // Shift+G(또는 툴바 Group 버튼): 모달 건너뛰고 즉시 자동 그룹 생성.
  // 정책:
  //   - groupName: 항상 'l-light' (조명 라인 그룹)
  //   - objectName: 그래프 내 기존 'light-N' 중 max(N) + 1 — 비어있으면 light-1
  //   - objectType: ObjectType.name === '조명 프레임' 자동 매칭
  // 사용자가 입력할 게 없으므로 모달 자체를 띄우지 않는다.
  const handleGroup = useCallback(() => {
    if (!graph) return
    // 2개 미만이면 그룹화 의미 없음 — 침묵 무시
    if (!canGroup(selectedElementIdsArray)) return

    // 1) 다음 light-N 번호 산출: 그래프 내 모든 'l-light' 그룹 (또는 layer='l-light' 요소)
    //    중 properties.name 이 'light-{digits}' 패턴인 것의 최댓값 +1
    const lightNameRe = /^light-(\d+)$/
    let maxNum = 0
    graph.getElements().forEach(el => {
      const data = el.get('data') as { isGroup?: boolean; layer?: string; groupName?: string; properties?: { name?: string } } | undefined
      if (!data) return
      const inLightFamily = data.isGroup
        ? data.groupName === 'l-light' || data.layer === 'l-light'
        : data.layer === 'l-light'
      if (!inLightFamily) return
      const name = data.properties?.name
      if (!name) return
      const m = lightNameRe.exec(String(name))
      if (m) {
        const n = parseInt(m[1], 10)
        if (Number.isFinite(n) && n > maxNum) maxNum = n
      }
    })
    const nextObjectName = `light-${maxNum + 1}`

    // 2) ObjectType '조명' 매칭 (구버전은 '조명 프레임' 이었음 — 호환 fallback).
    //    auto-link 매핑이 'lights' 템플릿 키와 1:1 정렬되도록 displayName '조명' 의
    //    ObjectType 에 그룹을 귀속시킨다.
    const lightType =
      types.find(t => t.name === '조명') ||
      types.find(t => t.name === '조명 프레임')
    if (!lightType) {
      // ObjectType 미존재 — 사용자에게 알리고 진행 (typeId null)
      console.warn('[handleGroup] ObjectType "조명" not found; creating group without type assignment')
    }
    const typeColor = lightType ? (lightType.icon || lightType.color || null) : null

    // 3) 그룹 생성
    const groupId = groupElements(selectedElementIdsArray, {
      groupName: 'l-light',
      objectName: nextObjectName,
      typeId: lightType?.id ?? null,
      typeColor,
    })
    if (!groupId) return

    // 4) objectsByLayer 동기화 (사이드바 즉시 반영)
    const groupCell = graph.getCell(groupId)
    if (groupCell?.isElement()) {
      setObjectsByLayer(prev => {
        const next = new Map(prev)
        const groupedIds = new Set(selectedElementIdsArray.map(id => id as string))
        for (const [layer, elements] of next.entries()) {
          const filtered = elements.filter(el => !groupedIds.has(el.id as string))
          if (filtered.length > 0) {
            next.set(layer, filtered)
          } else {
            next.delete(layer)
          }
        }
        const existing = next.get('l-light') || []
        next.set('l-light', [...existing, groupCell as dia.Element])
        return next
      })
    }
    setSelectedElementId(groupId)
    setSelectedElementIds(new Set())
  }, [graph, canGroup, selectedElementIdsArray, types, groupElements, setObjectsByLayer, setSelectedElementId, setSelectedElementIds])

  const handleUngroup = useCallback(() => {
    if (!selectionState.elementId) return
    ungroupElements(selectionState.elementId)
    setSelectedElementId(null)
    setSelectedElementIds(new Set())
  }, [ungroupElements, selectionState.elementId, setSelectedElementId, setSelectedElementIds])

  // 조명 자동 그룹핑 — 툴바 버튼 클릭 시 미리보기 모달 오픈.
  const handleOpenAutoGroupLights = useCallback(() => {
    setAutoGroupPreview(buildAutoGroupPreview())
  }, [buildAutoGroupPreview])

  // 모달 "진행" 클릭 시 클러스터별 그룹 일괄 생성/병합.
  //   1) 시작 light-N 번호 산출 (그래프 전체 스캔)
  //   2) ObjectType '조명' 1회만 해결
  //   3) 각 클러스터마다 분기:
  //      a. orphan 만 있음        → groupElements (신규 그룹)
  //      b. orphan + 1개 기존 그룹 → addToGroup (기존 그룹이 흡수)
  //      c. orphan + 2개 이상 그룹 → 첫 그룹을 anchor 로 두고
  //                                  나머지 그룹들을 ungroupElements 해서 primitive 로
  //                                  되돌린 뒤, anchor 에 addToGroup
  //   4) objectsByLayer 를 한 번만 batch 업데이트
  //      - 신규 group cell 들 추가
  //      - 흡수된 orphan/병합된 기존 그룹 제거
  const handleAutoGroupLightsConfirm = useCallback(
    (clusters: string[][]) => {
      if (!graph || clusters.length === 0) {
        setAutoGroupPreview(null)
        return
      }

      // 1) 시작 번호
      const lightNameRe = /^light-(\d+)$/
      let maxNum = 0
      graph.getElements().forEach((el) => {
        const data = el.get('data') as
          | { isGroup?: boolean; layer?: string; groupName?: string; properties?: { name?: string } }
          | undefined
        if (!data) return
        const inLightFamily = data.isGroup
          ? data.groupName === 'l-light' || data.layer === 'l-light'
          : data.layer === 'l-light'
        if (!inLightFamily) return
        const name = data.properties?.name
        if (!name) return
        const m = lightNameRe.exec(String(name))
        if (m) {
          const n = parseInt(m[1], 10)
          if (Number.isFinite(n) && n > maxNum) maxNum = n
        }
      })

      // 2) ObjectType (handleGroup 와 동일한 매칭)
      const lightType =
        types.find((t) => t.name === '조명') ||
        types.find((t) => t.name === '조명 프레임')
      const typeColor = lightType ? (lightType.icon || lightType.color || null) : null

      // 헬퍼: id 가 그래프 상 기존 라이트 그룹인지 판정.
      const isLightGroupId = (id: string): boolean => {
        const cell = graph.getCell(id)
        if (!cell?.isElement()) return false
        const data = cell.get('data') as { isGroup?: boolean; groupName?: string; layer?: string } | undefined
        if (!data?.isGroup) return false
        return (
          data.groupName === 'l-light' ||
          data.groupName === 'l-lightingline' ||
          data.layer === 'l-light'
        )
      }

      // 3) 클러스터별 분기 처리
      const createdGroupCells: dia.Element[] = []
      const removedFromLayers = new Set<string>()
      let counter = maxNum

      for (const cluster of clusters) {
        if (cluster.length < 2) continue

        // orphan vs 기존 그룹 분리.
        const groupIds: string[] = []
        const orphanIds: string[] = []
        for (const id of cluster) {
          if (isLightGroupId(id)) groupIds.push(id)
          else orphanIds.push(id)
        }

        if (groupIds.length === 0) {
          // a. orphan-only → 신규 그룹
          counter += 1
          const groupId = groupElements(orphanIds, {
            groupName: 'l-light',
            objectName: `light-${counter}`,
            typeId: lightType?.id ?? null,
            typeColor,
          })
          if (!groupId) {
            counter -= 1
            continue
          }
          for (const id of orphanIds) removedFromLayers.add(id)
          const cell = graph.getCell(groupId)
          if (cell?.isElement()) createdGroupCells.push(cell as dia.Element)
          continue
        }

        if (groupIds.length === 1) {
          // b. 1개 기존 그룹 + orphan → 흡수
          if (orphanIds.length === 0) continue
          addToGroup(groupIds[0], orphanIds)
          for (const id of orphanIds) removedFromLayers.add(id)
          continue
        }

        // c. 2개 이상 기존 그룹 → 첫 그룹 anchor 로 두고 나머지 ungroup 후 흡수
        const [anchorId, ...rest] = groupIds
        const toAbsorb: string[] = [...orphanIds]
        for (const gid of rest) {
          const restoredIds = ungroupElements(gid)
          toAbsorb.push(...restoredIds)
          // 사라진 그룹 셀은 l-light 레이어 목록에서 제거 필요.
          removedFromLayers.add(gid)
        }
        if (toAbsorb.length > 0) {
          addToGroup(anchorId, toAbsorb)
        }
        for (const id of orphanIds) removedFromLayers.add(id)
      }

      // 4) objectsByLayer 배치 업데이트
      if (createdGroupCells.length > 0 || removedFromLayers.size > 0) {
        setObjectsByLayer((prev) => {
          const next = new Map(prev)
          for (const [layer, elements] of next.entries()) {
            const filtered = elements.filter((el) => !removedFromLayers.has(el.id as string))
            if (filtered.length > 0) {
              next.set(layer, filtered)
            } else {
              next.delete(layer)
            }
          }
          if (createdGroupCells.length > 0) {
            const existing = next.get('l-light') || []
            next.set('l-light', [...existing, ...createdGroupCells])
          }
          return next
        })
      }

      setSelectedElementId(null)
      setSelectedElementIds(new Set())
      setAutoGroupPreview(null)
    },
    [graph, types, groupElements, addToGroup, ungroupElements, setObjectsByLayer, setSelectedElementId, setSelectedElementIds],
  )

  useKeyboardShortcuts(
    selectionState.elementId,
    selectedElementIdsArray,
    graph,
    undo,
    redo,
    handleElementDelete,
    setElementCount,
    false, // Always enable shortcuts in edit mode
    handleGroup,
    handleUngroup,
    handleCopy,
    handlePaste
  )

  useThemeSync(paper, theme)

  // Smart Guides - Figma/Illustrator-style alignment guides
  useSmartGuides(paper, graph)

  // Use hybrid rendering (Canvas for static, SVG for dynamic)
  const { forceRender, renderReady, canvasHitTest, getCanvasElements } = useHybridRendering(
    graph,
    paper,
    canvasRef.current,
    setElementCount,
    setObjectsByLayer,
    setLoadedFileName,
    pendingGraphJson,
    setPendingGraphJson,
    isRestoring,
    {
      enabled: true,
      staticThreshold: 500  // Use hybrid if >500 static elements
    }
  )

  // 객체 드래그 이동은 어떤 도구에서도 막는다 — 위치는 도면 추출값 그대로 둔다.
  useEffect(() => {
    if (!paper) return
    paper.setInteractivity(() => ({
      elementMove: false,
      addLinkFromMagnet: false,
    }))
  }, [paper])

  // Native pointer click selection — single source of truth for canvas click events.
  // Placed after useHybridRendering so renderReady ref is available.
  useCanvasClickSelection(paper, graph, canvasRef, handleCanvasSelect, handleBlankClick, currentTool, renderReady, handleCanvasToggleSelect, selectedElementIdsArray, canvasHitTest, getCanvasElements, handleSelectByLayer)

  // Multi-element move: when multiple elements are selected, dragging one moves all

  useObjectCreation(
    paper,
    graph,
    selectedObjectType,
    () => setSelectedObjectType(null)
  )

  // Zone polygon drawing (map-zone-polygon feature)
  // 완료 시 ZoneCreateModal 띄워서 그룹/이름/색상 입력받은 뒤 finalizeDrawing 호출
  const [pendingZone, setPendingZone] = useState<PendingZoneInput | null>(null)
  const {
    completeDrawing: completeZoneDrawing,
    finalizeDrawing: finalizeZoneDrawing,
    cancelDrawing: cancelZoneDrawing,
  } = useZonePolygonDrawing({
    paper,
    graph,
    onError: (err) => {
      // Log via existing logger; surface a non-blocking notice until a
      // toast layer is wired up. Alert kept as final user-visible
      // fallback so the failure is not silently dropped.
      log.error(LogCategory.API, 'Zone 저장 실패', { message: err.message })
      // eslint-disable-next-line no-alert -- no toast infra yet; tracked as Known Issue
      window.alert(`Zone 저장 실패: ${err.message}`)
    },
    onRequestFinalize: (pending) => setPendingZone(pending),
    floorName: currentFloorName,
    onSuccess: (cellId) => {
      // 우측 객체 리스트(objectsByLayer)에 추가 — handleGroup 패턴 동일.
      // 새로 추가된 polygon cell 을 ZONE 레이어 그룹에 매달아 사이드바 즉시 반영.
      if (!graph) return
      const cell = graph.getCell(cellId)
      if (!cell?.isElement()) return
      const data = cell.get('data') as { layer?: string } | undefined
      // ZONE_LAYER_NAME 과 동일 ('zones'). 신규 데이터는 항상 data.layer 가
      // 채워져 있고, 이 fallback 은 data 가 비어 있는 예외적 케이스 대비.
      const layerKey = data?.layer || 'zones'
      setObjectsByLayer(prev => {
        const next = new Map(prev)
        const existing = next.get(layerKey) || []
        // dedup: 같은 cell 이 이미 들어있으면 skip
        if (existing.some(e => e.id === cell.id)) return prev
        next.set(layerKey, [...existing, cell as dia.Element])
        return next
      })

      // 겹침 경고 — 막지는 않는다. 구역 소속의 정답은 memberIds 라 폴리곤이
      // 겹쳐도 기능은 멀쩡하고, 도면에 따라 일부러 겹쳐야 할 때가 있다.
      // 다만 의도치 않게 겹친 걸 모르고 지나가지 않도록 알려는 준다.
      const drawnPolygon = getZonePolygon(cell as dia.Element)
      const overlapping = findZoneElements(graph)
        .filter(z => z.id !== cell.id)
        .filter(z => polygonsOverlap(drawnPolygon, getZonePolygon(z)))
      if (overlapping.length > 0) {
        const names = overlapping.map(z => {
          const d = z.get('data') as { properties?: { name?: string } } | undefined
          return d?.properties?.name || String(z.id)
        })
        setExcelSnackbar({
          open: true,
          severity: 'info',
          message: `구역이 겹칩니다 — ${names.join(', ')} (저장은 그대로 진행됩니다)`,
        })
      }
    },
  })

  // 매뉴얼 placement (useObjectCreation), drag-drop, undo/redo 등으로
  // 새 element 가 graph 에 추가/삭제될 때 objectsByLayer 우측 사이드 패널이
  // 즉시 반영되도록 graph 이벤트를 구독해 동기화한다.
  // CSV-import 흐름(setObjectsByLayer 직접 호출) 과 함께 동작하며, dedup 으로
  // 중복 push 를 막아 동일 cell 이 다시 들어오는 것을 방지.
  useEffect(() => {
    if (!graph) return
    const elementLayerKey = (cell: dia.Cell): string => {
      const data = (cell.get('data') || {}) as { layer?: string; objectType?: string; typeId?: string }
      return data.layer || data.objectType || data.typeId || 'misc'
    }
    const handleAdd = (cell: dia.Cell) => {
      if (!cell.isElement()) return
      const key = elementLayerKey(cell)
      setObjectsByLayer(prev => {
        const existing = prev.get(key)
        if (existing?.some(e => e.id === cell.id)) return prev
        const next = new Map(prev)
        next.set(key, [...(existing || []), cell as dia.Element])
        return next
      })
    }
    const handleRemove = (cell: dia.Cell) => {
      if (!cell.isElement()) return
      const key = elementLayerKey(cell)
      setObjectsByLayer(prev => {
        const existing = prev.get(key)
        if (!existing) return prev
        const filtered = existing.filter(e => e.id !== cell.id)
        const next = new Map(prev)
        if (filtered.length === 0) next.delete(key)
        else next.set(key, filtered)
        return next
      })
    }
    graph.on('add', handleAdd)
    graph.on('remove', handleRemove)
    return () => {
      graph.off('add', handleAdd)
      graph.off('remove', handleRemove)
    }
  }, [graph])

  // Zone ObjectType.color 자동 추출 (모달의 추천 색상으로 사용)
  const zoneSuggestedColor = useMemo(() => {
    if (!pendingZone) return undefined
    const drawingState = useZoneDrawingStore.getState()
    const tid = drawingState.objectTypeId
    if (tid == null) return undefined
    const matched = types.find(t => Number(t.id) === tid)
    return matched?.color || matched?.icon || undefined
  }, [pendingZone, types])

  const handleZoneModalConfirm = useCallback(
    (input: { name: string; group: string; color: string }) => {
      setPendingZone(null)
      void finalizeZoneDrawing(input)
    },
    [finalizeZoneDrawing]
  )

  const handleZoneModalCancel = useCallback(() => {
    setPendingZone(null)
    cancelZoneDrawing()
  }, [cancelZoneDrawing])

  useObjectTypeSync(graph, paper)

  useProjectInitializer()

  // Relationship edit mode highlighting and click handlers
  useRelationshipEditMode({
    graph,
    paper,
    relationshipEditState,
    selectedElementId: selectionState.elementId,
    mutableRelationTypes,
    handleObjectUpdate,
    setRelationshipEditState,
  })

  // Canvas(플래그 ON) 관계 편집: 오버레이의 대상 클릭을 paper 경로와 동일한
  // 공유 코어(applyRelationLinkToTarget)로 처리. 매 렌더 최신 값으로 갱신.
  useEffect(() => {
    relationTargetClickRef.current = (clickedId: string) => {
      if (!graph || !selectionState.elementId) return
      applyRelationLinkToTarget({
        graph,
        relationshipEditState,
        selectedElementId: selectionState.elementId,
        mutableRelationTypes,
        handleObjectUpdate,
        setRelationshipEditState,
        clickedId,
      })
    }
  })

  // Priority z-index management hook — triggers Canvas2D re-render on z-index change
  usePriorityZIndex({ graph, types, mappings, onUpdate: forceRender })

  // Initialize status service for real-time status updates
  useStatusInitialization({ graph, elementCount })

  // Handlers
  const handleUploadClick = () => {
    handleUploadClickFn({ types, setModalState, triggerFileUpload })
  }

  const handleBackToProjects = () => {
    handleBackToProjectsFn({ queryClient, graph, handleSave, navigate, urlProjectId })
  }

  const handleSaveModalConfirm = () => {
    handleSaveModalConfirmFn({ autoNavigate, setModalState, setAutoNavigate, navigate, urlProjectId })
  }

  // Countdown timer for save modal (only when autoNavigate is enabled)
  const { saveCountdown } = useSaveCountdown({
    modalState,
    setModalState,
    autoNavigate,
    urlProjectId,
  })

  // Check if map data exists
  const hasMapData = mapDataArray && mapDataArray.length > 0

  return (
    <div className={styles.container}>
      {/* 표준 헤더 — 다른 페이지와 동일한 ProjectPageHeader (현장 목록 › 현장 › 맵 에디터).
          맵 편집 툴바(EditorHeader) 위에 공통 네비게이션 헤더를 둔다.
          가이드 진행 중에는 셋업 페이지와 같은 라벨을 유지해 화면이 "에디터로
          넘어간" 것처럼 보이지 않게 한다. */}
      <ProjectPageHeader
        currentPageLabel={guideActive ? '가이드 맵 생성' : '맵 에디터'}
        currentPageIcon={<MapIcon fontSize="inherit" />}
      />
      <input
        type="file"
        ref={csvInputRef}
        onChange={handleFileChange}
        accept=".csv"
        style={{ display: 'none' }}
      />
      {/* 맵 편집 툴바 — 가이드 진행 중에는 숨긴다. 셋업 페이지에서 이어지는
          위저드 화면(공통 헤더 + 가이드 카드/배너)이 끝(가이드 종료/완료)까지
          유지되고, 각 단계에 필요한 조작은 가이드 카드의 버튼과 모달이 담당한다. */}
      {!guideActive && (
      <EditorHeader
        loadedFileName={loadedFileName}
        zoom={zoom}
        theme={theme}
        hasObjectTypes={types.length > 0}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        hasMapData={elementCount > 0}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onRotate={handleRotate}
        onFitToScreen={handleFitToScreen}
        onUploadClick={handleUploadClick}
        onSave={handleSave}
        onClearCanvas={handleClearCanvas}
        onExport={handleExport}
        onThemeToggle={toggleTheme}
        onBackToProjects={handleBackToProjects}
        toolbarExtras={
          <>
            <ExcelMenu
              graph={graph}
              objectTypes={types}
              mapName={currentLotData?.name || loadedFileName || 'untitled'}
              resolveTypeId={resolveTypeIdForExport}
              onMessage={handleExcelMessage}
              onFileSelected={handleExcelFileSelected}
              onEditObjects={() => setPropertySheetOpen(true)}
              disabled={types.length === 0}
            />
            <div className={toolbarButtonStyles.container}>
              <button
                type="button"
                className={toolbarButtonStyles.trigger}
                onClick={handleCctvIpFromDxf}
                disabled={!graph || mappingStep !== 'idle' || cctvIpBusy}
                title="DXF 변환 시 저장된 CCTV IP를 배치된 CCTV 와 정렬해 IP 만 적용 (이름·구역은 건드리지 않음)"
              >
                📷 CCTV IP 매핑
              </button>
            </div>
            <div className={toolbarButtonStyles.container}>
              <button
                type="button"
                className={toolbarButtonStyles.trigger}
                onClick={handleDongFromDxf}
                disabled={!graph || mappingStep !== 'idle' || dongBusy}
                title="DXF 변환 시 저장된 동/건물 라벨(전용 레이어 큰 글자)을 CCTV IP 정렬로 맵에 텍스트 객체로 배치 — CCTV IP 매핑이 먼저 필요"
              >
                🏢 동 가져오기
              </button>
            </div>
            <div className={toolbarButtonStyles.container}>
              <button
                type="button"
                className={toolbarButtonStyles.trigger}
                onClick={() => setZoneAutoGenOpen(true)}
                disabled={!graph || mappingStep !== 'idle' || zoneAutoGenBusy}
                title="NVR 저장 용량 기준으로 구역 미배정 CCTV를 용량 한도까지 최대로 묶어 구역(원)을 만들고, 구역별로 CCTV 이름을 순차 부여 (도면 PDF 불필요)"
              >
                ⭕ 구역 자동 생성
              </button>
            </div>
            <div className={toolbarButtonStyles.container}>
              <button
                type="button"
                className={toolbarButtonStyles.trigger}
                onClick={() => {
                  // 관계선과 맵 클릭(잇기·빼기)은 편집 캔버스에서 동작한다 — 다시 들어온
                  // 맵은 뷰어 모드라 편집 캔버스가 숨겨져 있으므로 에디터 모드로 바꾼다.
                  if (viewMode === 'viewer') setViewMode('edit')
                  setDrawingCheckOpen(false)
                  setCctvRelOpen(prev => !prev)
                }}
                disabled={!graph}
                title="CCTV 관계(조명 순차 점등 체인) — 맵 좌표로 좌/우 이웃을 만들고 고친 뒤 JSON·SQL 로 내보낸다"
              >
                🔀 CCTV 관계
              </button>
            </div>
            <div className={toolbarButtonStyles.container}>
              <button
                type="button"
                className={toolbarButtonStyles.trigger}
                onClick={() => {
                  // 도면에만 있는 자리·맵에만 있는 객체 고리는 편집 캔버스에 그려진다.
                  if (viewMode === 'viewer') setViewMode('edit')
                  setCctvRelOpen(false)
                  setDrawingCheckOpen(prev => !prev)
                }}
                disabled={!graph || !urlProjectId}
                title="도면 대조 — 이 층을 만든 도면을 다시 읽어 주차면·CCTV·비상벨·기둥 수량과 위치를 맵과 맞춰 본다"
              >
                📏 도면 대조
              </button>
            </div>
            <div className={toolbarButtonStyles.container}>
              <button
                type="button"
                className={toolbarButtonStyles.trigger}
                onClick={() => setTypePanelOpen(prev => !prev)}
                title="객체 타입 패널 열기/닫기 — 타입 추가·수정과 캔버스 배치"
              >
                🧩 객체 타입
              </button>
            </div>
            <div className={toolbarButtonStyles.container}>
              <button
                type="button"
                className={toolbarButtonStyles.trigger}
                onClick={() => setIpPrefixDialogOpen(true)}
                title="현장 CCTV IP 앞자리(예: 172.19) 설정 — DXF 변환 시 CCTV IP 자동 추출에 사용. 저장 후 DXF 변환을 다시 실행해야 반영"
              >
                📶 CCTV IP 설정
              </button>
            </div>
            <div className={toolbarButtonStyles.container}>
              <button
                type="button"
                className={toolbarButtonStyles.trigger}
                onClick={handleOpenAutoLinkModal}
                disabled={!graph}
                title="구역–CCTV는 ID의 구역 번호로, 다른 관계는 반경으로 자동 연결하고 결과를 속성 시트에서 수정"
              >
                🔗 자동 관계 생성
              </button>
            </div>
            ;<ResetRelationshipsControl
              key={currentFloor}
              disabled={!graph || mappingStep !== 'idle' || zoneAutoGenBusy}
              selectedCount={
                new Set([
                  ...selectedElementIdsArray,
                  ...(selectionState.elementId ? [selectionState.elementId] : []),
                ]).size
              }
              relationTypes={mutableRelationTypes}
              onReset={(scope, pairs) => {
                if (!graph) return
                checkpoint()
                const changed = resetRelationships(
                  graph,
                  scope,
                  [
                    ...selectedElementIdsArray,
                    ...(selectionState.elementId ? [selectionState.elementId] : []),
                  ],
                  mutableRelationTypes,
                  pairs,
                )
                checkpoint()
                setRelationshipEditState({
                  editing: false,
                  relationKey: null,
                  propertyKey: null,
                  targetIds: [],
                })
                getActiveCanvasRenderer()?.setAutoLinkResultOverlay(null)
                setDataVersion((version) => version + 1)
                forceRender()
                setExcelSnackbar({
                  open: true,
                  severity: 'success',
                  message:
                    changed > 0
                      ? `${changed}개 객체의 관계를 초기화했습니다. 맵을 저장해 주세요.`
                      : '초기화할 관계가 없습니다.',
                })
              }}
            />
          </>
        }
      />
      )}

      {/* Blocking busy overlay: map data load / CCTV & 구역 통합 매핑 단계별 */}
      {(isMapDataLoading || mappingStep !== 'idle' || cctvIpBusy) && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', flexDirection: 'column', gap: '1rem',
          alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, color: '#fff',
          fontSize: '1.1rem',
        }}>
          <CircularProgress size={40} sx={{ color: '#fff' }} />
          {isMapDataLoading
            ? '맵 데이터를 불러오는 중...'
            : cctvIpBusy
              ? 'CCTV IP 매핑 중... (서버 CCTV IP 적용)'
            : mappingStep === 'ip'
              ? 'CCTV & 구역 매핑 (1/3) — CCTV IP 적용 중...'
              : mappingStep === 'name'
                ? 'CCTV & 구역 매핑 (2/3) — CCTV 이름 매핑 중... (도면 PDF 분석)'
                : 'CCTV & 구역 매핑 (3/3) — 구역 생성 중... (도면 PDF 분석)'}
        </div>
      )}

      <main className={styles.main}>
        {/* 좌측 객체 타입 패널 — 기본 숨김, 툴바 "🧩 객체 타입" 버튼으로 토글.
            우측 패널은 렌더링하지 않는다(객체 편집은 Excel 메뉴의 "객체 수정" 시트). */}
        {typePanelOpen && viewMode === 'edit' && (
          <ResizablePanel side="left" defaultWidth={360} minWidth={200} maxWidth={500}>
            <ObjectTypeSidebar
              selectedTypeId={selectionState.typeId}
              onSelectType={handleTypeSelect}
              projectId={projectId}
            />
          </ResizablePanel>
        )}
        {cctvRelOpen && (
          <CctvRelationPanel
            graph={graph}
            paper={paper}
            strokeWidth={computeRelationStrokeWidth(graph)}
            onClose={() => setCctvRelOpen(false)}
          />
        )}
        {drawingCheckOpen && urlProjectId && (
          <DrawingCheckPanel
            key={String(currentFloor)}
            graph={graph}
            siteId={parseInt(urlProjectId, 10)}
            csvFileName={mapDataGetCsvFileName(floors.find(f => String(f.id) === String(currentFloor))?.mapData)}
            csvRawData={mapDataGetCsvRaw(floors.find(f => String(f.id) === String(currentFloor))?.mapData)}
            onClose={() => setDrawingCheckOpen(false)}
          />
        )}
        {/* Canvas Area - Center */}
        <CanvasArea
          canvasRef={canvasRef}
          mapId={mapId}
          rawData={rawData}
          parsedData={parsedData}
          currentFloor={currentFloor}
          setModalState={setModalState}
          graph={graph}
          paper={paper}
          isRestoring={isRestoring}
          isSaving={isSaving}
          currentTool={currentTool}
          onToolChange={setCurrentTool}
          canGroup={canGroupSelection}
          isGroupSelected={isGroupSelected}
          onGroup={handleGroup}
          onUngroup={handleUngroup}
          onAutoGroupLights={handleOpenAutoGroupLights}
          labelToggles={labelToggles}
          onLabelTogglesChange={setLabelToggles}
          viewMode={viewMode}
        />

        {/* 우측 사이드패널 — 객체 리스트/속성/관계. 에디터 모드에서만 표시. */}
        {viewMode === 'edit' && (
          <ResizablePanel side="right" defaultWidth={300} minWidth={200} maxWidth={400}>
            <EditorSidebar
              loadedFileName={loadedFileName}
              elementCount={elementCount}
              zoom={zoom}
              objectsByLayer={objectsByLayer}
              selectedElementId={selectionState.elementId}
              onObjectClick={(id) => setSelectedElementId(id || null)}
              onObjectUpdate={handleObjectUpdate}
              graph={graph}
              template={template}
              relationTypes={mutableRelationTypes}
              onUnlink={handleUnlink}
              onAutoLink={handleAutoLink}
              onAutoLinkAll={handleOpenAutoLinkModal}
              onUpdateRelationType={handleUpdateRelationType}
              onDeleteRelationType={handleDeleteRelationType}
              onRelationEditModeChange={handleRelationEditModeChange}
            />
          </ResizablePanel>
        )}

        {/* Multi Selection Panel */}
        <MultiSelectionPanel
          selectedElementIds={selectedElementIdsArray}
          graph={graph}
          onRemoveElement={(elementId) => handleCanvasToggleSelect(elementId, false)}
          onClearAll={() => {
            clearMultiSelection()
            setSelectedElementId(null)
          }}
        />

        {/* 객체 속성 시트 — 우측 절반 분할(캔버스를 왼쪽으로 밀어냄).
            Excel 메뉴 "객체 수정" 과 자동 관계 생성 적용 직후 열린다.
            시트 렌더가 크래시해도 맵(캔버스)이 하얗게 사라지지 않게 격리한다. */}
        <EditorErrorBoundary label="속성 시트" onReset={() => setPropertySheetOpen(false)}>
          <PropertySheetModal
            isOpen={propertySheetOpen}
            onClose={() => setPropertySheetOpen(false)}
            graph={graph}
            objectTypes={types}
            relationTypes={mutableRelationTypes}
            resolveTypeId={resolveTypeIdForExport}
            onObjectUpdate={handleObjectUpdate}
            onFocusObject={handleSheetFocusObject}
            onSave={handleSheetSave}
            isSaving={isSaving}
          />
        </EditorErrorBoundary>
      </main>

      {/* Zone polygon drawing HUD (fixed overlay) */}
      <ZoneDrawingHud
        onComplete={completeZoneDrawing}
        onCancel={cancelZoneDrawing}
      />

      {/* 안내현황판 좌/우 영역 드래그 HUD + 진입 버튼 */}
      <GuideBoardDirectionHud
        phase={guideBoardDirectionLink.phase}
        onCancel={guideBoardDirectionLink.cancel}
      />
      {guideBoardDirectionLink.phase === 'idle' &&
        (() => {
          const sid = selectionState.elementId
          if (!sid || !graph) return null
          const el = graph.getCell(sid)
          if (!el || !el.isElement() || !isGuideBoardElement(el as dia.Element)) {
            return null
          }
          return <GuideBoardLinkButton onStart={() => guideBoardDirectionLink.start(sid)} />
        })()}

      {/* 구역 선택 시 "다각형 다시 그리기" 진입 버튼.
          자동 생성은 카메라 무게중심 기준 원(32각형)으로 구역을 잡는데,
          도면의 실제 구획과는 다를 수 있다. 소속(memberIds)은 그대로 두고
          모양만 직접 그려 고치는 경로. */}
      {(() => {
        const sid = selectionState.elementId
        if (!sid || !graph) return null
        if (useZoneDrawingStore.getState().mode !== 'idle') return null
        const el = graph.getCell(sid)
        if (!el || !el.isElement()) return null
        const data = el.get('data') as
          | { typeId?: number; properties?: { kind?: string; name?: string } }
          | undefined
        if (data?.properties?.kind !== 'zone') return null
        const zoneName = data.properties.name || String(sid)
        // 다시 그리기는 기존 cell 을 고치는 것이라 objectTypeId 가 필요 없지만,
        // 스토어 컨텍스트가 요구하므로 셀에 박힌 값을 그대로 넘긴다.
        const typeId = data.typeId ?? -1
        return (
          <ZoneRedrawButton
            zoneName={zoneName}
            onStart={() => {
              if (!currentFloor || projectId == null) return
              useZoneDrawingStore.getState().start({
                objectTypeId: typeId,
                projectId,
                floorId: currentFloor,
                replaceCellId: String(sid),
              })
            }}
          />
        )
      })()}

      {/* Zone create modal — 완료 누르면 그룹/이름/색상 입력 받음 */}
      {pendingZone && (
        <ZoneCreateModal
          isOpen={true}
          suggestedName={pendingZone.suggestedName}
          existingZoneNames={pendingZone.existingZoneNames}
          pointCount={pendingZone.points.length}
          suggestedColor={zoneSuggestedColor}
          onConfirm={handleZoneModalConfirm}
          onCancel={handleZoneModalCancel}
        />
      )}

      {/* Excel I/O notification toast */}
      <Snackbar
        open={excelSnackbar.open}
        autoHideDuration={excelSnackbar.severity === 'error' ? null : 3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={excelSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {excelSnackbar.message}
        </Alert>
      </Snackbar>

      {/* 구역 자동 생성 — NVR 용량으로 미배정 CCTV 를 묶고 이름을 순차 부여 */}
      <ZoneAutoGenerateDialog
        open={zoneAutoGenOpen}
        pending={zoneAutoGenInfo.pending}
        total={zoneAutoGenInfo.total}
        existingZoneCount={zoneAutoGenInfo.existingZoneCount}
        defaultPrefix={zoneAutoGenInfo.defaultPrefix}
        busy={zoneAutoGenBusy}
        onRun={handleZoneAutoGenerate}
        onClose={() => setZoneAutoGenOpen(false)}
      />

      {/* CCTV IP 앞자리 설정 — 툴바 버튼 또는 [1/3 IP] 실패 시 자동 오픈 */}
      <CctvIpPrefixDialog
        open={ipPrefixDialogOpen}
        initialPrefix={currentLotData?.cctvIpPrefix || ''}
        saving={ipPrefixSaving}
        onSave={handleSaveCctvIpPrefix}
        onClose={() => setIpPrefixDialogOpen(false)}
      />

      {/* 조명 자동 그룹핑 미리보기 모달 — 툴바 버튼이 트리거. */}
      {autoGroupPreview && (
        <AutoGroupLightsModal
          candidates={autoGroupPreview.candidates}
          suggestedThreshold={autoGroupPreview.suggestedThreshold}
          medianShortSide={autoGroupPreview.medianShortSide}
          clusterFn={clusterByThreshold}
          onConfirm={(clusters) => {
            handleAutoGroupLightsConfirm(clusters)
            // 가이드 위저드가 켜져 있으면 조명 그룹핑 단계 완료 통지(아니면 no-op).
            useGuidedMapStore.getState().completeStep('lights')
          }}
          onCancel={() => setAutoGroupPreview(null)}
        />
      )}

      {/* All Modals - Consolidated */}
      <EditorModals
        modalState={modalState}
        setModalState={setModalState}
        autoNavigate={autoNavigate}
        setAutoNavigate={setAutoNavigate}
        saveCountdown={saveCountdown}
        onSaveModalConfirm={handleSaveModalConfirm}
        currentFloor={currentFloor}
        currentLotData={currentLotData}
        graph={graph}
        paper={paper}
        template={template}
        mutableRelationTypes={mutableRelationTypes}
        onAutoLinkConfirm={handleAutoLinkConfirmThenSheet}
        showSaveSuccessModal={showSaveSuccessModal}
        setShowSaveSuccessModal={setShowSaveSuccessModal}
        savedMapId={savedMapId}
        savedThumbnail={savedThumbnail}
        urlProjectId={urlProjectId}
        excelImportResult={excelImportResult}
        excelSkippedSheets={excelSkippedSheets}
        onExcelImportApply={handleExcelImportApply}
      />

      {/* 가이드 맵 프로젝트 생성 — 페이지 전환형 위저드(에디터 단계).
          가이드 렌더가 크래시해도 에디터 전체가 하얗게 사라지지 않게 격리한다. */}
      {guideActive && (
        <EditorErrorBoundary label="가이드">
        <EditorGuide
          mappingModalOpen={modalState.mapping}
          elementCount={elementCount}
          cctvMappingStep={mappingStep}
          lightsModalOpen={autoGroupPreview !== null}
          relationsModalOpen={modalState.autoLink}
          propertySheetOpen={propertySheetOpen}
          saveSuccess={showSaveSuccessModal}
          isSaving={isSaving}
          onOpenAutoGroupLights={handleOpenAutoGroupLights}
          onPdfSelected={handleCctvZoneMapping}
          onOpenAutoLink={handleOpenAutoLinkModal}
          onSave={handleSave}
          onOpenPropertySheet={() => setPropertySheetOpen(true)}
          onOpenViewer={() => navigate(`/project/${urlProjectId}/map-viewer/${mapId}`)}
        />
        </EditorErrorBoundary>
      )}
    </div>
  )
}
